/**
 * DDR room stream + message-state service.
 *
 * Why a service: the chat panel can be destroyed mid-turn (user clicks
 * away to a different room or back to a CIQ thread) but the agent loop
 * keeps generating server-side. Holding the SSE consumer + message state
 * here lets the model reply complete in the background; when the user
 * reopens the room the finished turn is already there.
 *
 * One stream per room - re-sending while a prior stream is in flight is
 * a no-op until the prior stream completes (or the cancel safety net aborts it).
 */

import { Router } from '@angular/router';
import { computed, DestroyRef, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { Subscription } from 'rxjs';

import { ArchetypeCatalogEntry, CanvasDiffChunk, CanvasField, CanvasSession, Persona } from '../domainTypes/ddr';
import { UserPreferencesService } from './user-preferences.service';
import { AuthService } from './auth.service';
import { DdrService, RoomStreamEvent } from './ddr.service';
import { StreamRejectedError } from '../utils/streamRejectedError';
import { ThreadEventsService } from './thread-events.service';
import { StudioHandoffService } from './studio-handoff.service';

/** Full detail for one tool call, held in memory for the current page session
 *  only (ADR-0023). `args` also arrives on the persisted tool_calls JSON;
 *  `result` exists nowhere but the live stream. */
export type CapturedToolResult = {
    tool: string;
    args: Record<string, unknown> | null;
    result: unknown;
    isError: boolean;
};

export type RoomMessage = {
    id: number;
    sender_type: 'user' | 'ddr' | 'system';
    content: string;
    created_at?: string;
    persona_id?: string;
    persona_display_name?: string;
    routing_meta?: Record<string, unknown> | null;
    /** The open incognito segment this row belongs to (ADR-0041), or null for
     *  an on-the-record row. Only open-segment rows are ever served, so a
     *  non-null value always means "inside the grey box". */
    incognito_segment_id?: number | null;
    /** Tool-call breadcrumbs surfaced from the agent stream. `running` is
     *  true between `tool_call_start` and `tool_result`. Two outcome fields,
     *  two audiences (ADR 0023): `result_label` is the plain-language text the
     *  chip renders; `result_preview` is the dense agent-facing digest, shown
     *  to users only in Developer Mode. */
    tool_calls?: {
        tool: string;
        /** Stable id for this call, present on both live events and the saved
         *  tool_calls JSON. The key the Tool-call inspector matches on. */
        tool_use_id?: string;
        result_label?: string;
        result_preview?: string;
        is_error?: boolean;
        running?: boolean;
        args_summary?: string;
        /** The one line the user reads while this call runs: the call's `step`
         *  argument, else run_code's first comment line. Also the bubble's
         *  working hint until the next step or the reply replaces it. */
        step?: string;
        /** Set when this call was refused because its source is incognito-only
         *  (ADR-0039). Drives the one-click "turn on incognito mode" offer.
         *  Arrives on the live `tool_result` event AND on the persisted
         *  tool_calls JSON, so the offer survives a history reload. */
        requires_incognito?: boolean;
        /** Plain name of the gated source ("Email", "castle-hr") — the only
         *  part of the refusal payload that reaches the chip. */
        source_name?: string;
    }[];
    /** Internal marker - when true, the next text_delta inserts a
     *  paragraph break first so consecutive text blocks don't fuse. */
    _newBlock?: boolean;
    /** True while text_delta events are still arriving for this turn. */
    streaming?: boolean;
    /** Set when the chained DDR turn has been kicked off but no content/tool
     *  has streamed yet - frontend shows "<persona> is working on this…" so
     *  the wait between routing_decision and first model event has visible
     *  context instead of bare typing dots. Cleared on first text_delta or
     *  tool_call_start. */
    thinkingHint?: string;
    /** Set while the agent has retracted the text on screen and is re-feeding
     *  the model. The draft stays visible, dimmed, under this status line, and
     *  the next text_delta REPLACES it (the server persists only the re-fed
     *  text). Cleared by that delta, by grounding_correction, and by the
     *  saved-message frame. */
    retractionHint?: string;
    /** True while this user message sits in the server's drain queue (sent
     *  during a live turn). Drives the "Queued" chip; cleared when the
     *  follow-up turn starts (queued_drain_start) or history reloads. */
    queued?: boolean;
};

type RoomState = {
    messages: RoomMessage[];
    sending: boolean;
    error: string | null;
    abort: AbortController | null;
    /** Set false once a paginated load returns fewer rows than requested.
     *  Drives the chat panel's scroll-up prefetch - once known-empty
     *  upstream, we stop firing the cursor query on every scroll. */
    hasMore: boolean;
    /** Guards re-entrant scroll-up loads. */
    loadingMore: boolean;
    /** Active Collaborate Canvas session for this room, or null when none. */
    canvasSession: CanvasSession | null;
    /** The text of a send the server refused with 409 (a turn already running,
     *  usually on another API task). The panel puts it back in the composer so
     *  the user resends instead of retyping; cleared once consumed. */
    rejectedText: string | null;
    /** Skills that entered the current or last turn: `/use` and pinned matches
     *  resolved by the server, plus any the DDR loaded itself via read_skill
     *  (`skill_resolved` frames). Reset at every turn boundary: a local send,
     *  a remote turn starting, and a queued-drain follow-up turn on either path,
     *  so a chip never describes the previous turn. */
    /** Skills the DDR loaded on the turn in flight (`skill_resolved` frames).
     *  Lives only while `sending`: cleared at send start and on every
     *  turn-finish path, so the "used this turn" chip never lingers under the
     *  composer after the reply landed (2026-09-11). Pinned skills are the
     *  persona's own list and are not in here. */
    skillsUsed: string[];
};

const EMPTY_STATE: RoomState = {
    messages: [],
    sending: false,
    error: null,
    abort: null,
    hasMore: true,
    loadingMore: false,
    canvasSession: null,
    rejectedText: null,
    skillsUsed: [],
};

/** Server cap on the GET /rooms/{id}/messages endpoint (`Query(le=200)`). */
const HISTORY_PAGE_SIZE = 200;

/** One frame of a live turn mirrored onto the user-events pipe (backend
 *  `ddr/turn_events.py`). `frame` has the exact shape the direct room stream
 *  carries, so both sources render through `handleEvent`. */
type RemoteTurnFrameEvent = {
    type: 'ddr_turn_frame';
    room_id: string;
    turn_id: string;
    /** Room-scoped generation from the shared counter (backend `turn_gen`),
     *  strictly increasing across a room's turns on every replica. Null or
     *  absent on a local-only turn or a frame from an older backend build:
     *  such a start frame never outranks a live turn. Wall clocks are not
     *  used: they are not monotonic across replicas. */
    turn_gen?: number | null;
    seq: number;
    frame: RoomStreamEvent;
};

/** A turn this tab follows through `ddr_turn_frame` events instead of its own
 *  stream: sent from another device or tab, or served by another replica after
 *  a refresh. `turnId` is null while the turn is only known from `turn_status`
 *  (no frame seen yet); the first frame's id is adopted. */
type RemoteTurn = {
    turnId: string | null;
    /** Generation of the turn being followed; null until a frame carries one. */
    turnGen: number | null;
    lastSeq: number;
    activeId: number;
    allTempIds: Set<number>;
    routedPersonaId: string | null;
    slotCreated: boolean;
    endPoll: ReturnType<typeof setInterval> | null;
};

/** What the mirror saw while this tab owned a direct stream. The direct turn's
 *  own frames are ignored (they would render twice), but a SUCCESSOR turn can
 *  begin before the direct stream tears down: the backend releases the room
 *  claim before the old stream delivers its final frame, so a turn started on
 *  another tab publishes its start frames while `abort` is still set here.
 *  Dropping them lost the whole turn (later deltas cannot start following);
 *  they are buffered and replayed once the direct stream ends. The direct
 *  turn's id is the first id seen while it is open; a drain follow-up runs
 *  under that same id, so it is never mistaken for a successor. */
type DirectStreamShadow = {
    ownTurnId: string | null;
    ownTurnGen: number | null;
    successorTurnId: string | null;
    successorGen: number | null;
    buffered: RemoteTurnFrameEvent[];
};
/** Frames kept per successor. Past this the turn's text is completed by the
 *  history reload on its `turn_ended`, as for any mid-turn joiner. */
const SUCCESSOR_BUFFER_MAX = 64;

/** A frame from a DIFFERENT turn id than the one followed is only a NEWER turn
 *  when it is a start-type frame with a strictly higher generation. A late
 *  delta, terminal or late start frame from a turn that lost its lease must not
 *  finish and reload over the successor's live turn. No generation (older
 *  backend build mid-deploy, local-only turn) is unknown, not newest: it is
 *  ignored while a live turn is followed. A followed turn whose own generation
 *  is unknown yields to the first generation-bearing start. */
function isNewerTurnStart(rt: RemoteTurn, e: RemoteTurnFrameEvent): boolean {
    if (!REMOTE_TURN_START_TYPES.has(e.frame.type)) {
        return false;
    }
    const gen = typeof e.turn_gen === 'number' ? e.turn_gen : null;
    if (gen === null) {
        return false;
    }
    return rt.turnGen === null || gen > rt.turnGen;
}

/** Frames that may START following a turn we have not seen yet. A stray late
 *  delta (pub/sub is unordered against the durable stream) or a terminal for a
 *  turn this tab streamed directly must never open a new streaming bubble.
 *  `skill_resolved` is emitted before routing on a turn that loads a skill
 *  and is durable and generation-fenced like the others, so it counts too;
 *  otherwise the first frame of such a turn was dropped and its chip lost. */
/** How long cancel() lets the direct stream stay silent after the Stop
 *  request settles before aborting it. The server's saved-partial frame
 *  normally lands within a second; this only has to beat a dead socket. */
const CANCEL_SILENCE_ABORT_MS = 8000;

const REMOTE_TURN_START_TYPES = new Set(['user_message_saved', 'routing_decision', 'text_start', 'queued_drain_start', 'skill_resolved']);
/** Agent events that mean "the text just streamed is being thrown away and the
 *  model is being re-fed"; keyed to the status the slot shows meanwhile. */
const DRAFT_RETRACTION_HINTS: Record<string, string> = {
    figures_nudge: 'Checking those figures against the data',
    ungrounded_claim_nudge: 'Verifying that before saying it',
    fake_tool_nudge: 'Running that now',
    promise_nudge: 'Doing that now instead of promising it',
};
const REMOTE_TURN_END_POLL_MS = 15_000;
/** Turn ids this tab has already finished, per room, so a late frame of any
 *  type from a retired turn is ignored. Bounded per room. */
const RETIRED_TURNS_KEPT = 20;

/** A block raised by a spend gate, from either of the two paths that carry one:
 *  the `spend_blocked` stream event (an admin quota, caught by the gate inside
 *  the agent loop) or a 402 on the stream POST (solvency, trial and plan blocks,
 *  caught by the router gate before the message is persisted). Both carry the
 *  same fields, so the modal does not care which refused the turn.
 *
 *  `limitKind` selects the case-specific copy: 'user_daily' | 'user_monthly' |
 *  'workspace_ceiling' | 'proactive_reserve' | 'wallet_missing' |
 *  'wallet_depleted' | 'wallet_insufficient' | 'trial_expired' |
 *  'trial_credit_limit' | 'plan_cap_reached' | 'plan_not_included' |
 *  'plan_restricted' | 'plan_expired'. Unknown kinds fall back to `reason`,
 *  which the server always sets. */
export type SpendBlockInfo = {
    limitKind: string | null;
    reason: string;
    /** When the block clears, in the server's coarse vocabulary: 'midnight' |
     *  'month' | ''. Read only by the fallback copy, for a `limitKind` this
     *  bundle does not recognise — the known cases word their own reset line. */
    resets: string;
    /** Structured plan detail (cap, window, resets_at, feature label), null for
     *  every non-plan block. Shape: `PlanBlock`, parsed through
     *  `SafePlanBlockSchema` by the modal so one drifted field cannot blank it. */
    planBlock: unknown;
};

/** Resolve as soon as the server has the message, or with the turn's result
 *  if it ends first. Handle it records its hand-off on this, not on the end
 *  of the DDR's turn: a reload during a long turn otherwise left the prompt
 *  in the DM with no resolve written and the button live (review, 2026-09-22). */
export function sendAccepted(
    stream: { send: (roomId: string, content: string, target: string | null, opts?: { onAccepted?: () => void }) => Promise<boolean> },
    roomId: string,
    content: string,
    targetPersonaId: string | null
): Promise<boolean> {
    return new Promise<boolean>(resolve => {
        const turn = stream.send(roomId, content, targetPersonaId, { onAccepted: () => resolve(true) });
        turn.then(ok => resolve(ok)).catch(() => resolve(false));
    });
}

/** How recently the user must have touched the page for a read mark to apply now. */
const PRESENCE_WINDOW_MS = 2 * 60 * 1000;

@Injectable({
    providedIn: 'root',
})
export class DdrRoomStreamService {
    private readonly ddr = inject(DdrService);
    private readonly router = inject(Router);
    private readonly auth = inject(AuthService);
    private readonly threadEvents = inject(ThreadEventsService);
    private readonly studioHandoff = inject(StudioHandoffService);
    private readonly userPreferences = inject(UserPreferencesService);

    private readonly stateByRoom = signal<Map<string, RoomState>>(new Map());
    private readonly remoteTurns = new Map<string, RemoteTurn>();
    private readonly retiredTurnIds = new Map<string, string[]>();
    private readonly directShadows = new Map<string, DirectStreamShadow>();
    /** Frames received on the room's direct stream so far. cancel() compares
     *  it before and after its silence window: a stream the server carried on
     *  after the Stop (saved partial, queued reply) keeps the count moving. */
    private readonly directFramesSeen = new Map<string, number>();
    private readonly personaMap = signal<Map<string, Persona>>(new Map());
    private readonly archetypeCatalog = signal<ArchetypeCatalogEntry[]>([]);

    /** App-level spend-limit block (drives the chat modal). Not per-room - the
     *  user is over budget everywhere, so one modal covers the whole chat. */
    private readonly _spendBlock = signal<SpendBlockInfo | null>(null);
    readonly spendBlock = this._spendBlock.asReadonly();

    dismissSpendBlock(): void {
        this._spendBlock.set(null);
    }

    // Bumped each time a turn finishes (the only moment the user's spend
    // changes). The budget banner watches this to re-check its thresholds -
    // e.g. a chat turn that crosses the proactive-reserve mark, which fires no
    // block event, must still flip the "background paused" banner on.
    private readonly _turnCompleted = signal(0);
    readonly turnCompleted = this._turnCompleted.asReadonly();

    // ── Tool-call inspector capture (ADR-0023) ───────────────────────
    //
    // Raw tool results are never persisted. They live HERE rather than on the
    // message objects because loadHistory() replaces `messages` wholesale on
    // every room entry. Keyed by tool_use_id, present on both the live events
    // and the persisted tool_calls JSON. A plain Map, not a signal.
    private readonly capturedResults = new Map<string, CapturedToolResult>();

    /** Bounded so a long session with a chatty agent can't grow the tab's
     *  memory without limit. Oldest-first eviction; losing the oldest detail
     *  in a marathon session is strictly better than a leak. */
    private static readonly MAX_CAPTURED_RESULTS = 400;

    /** A COUNT cap alone is not a memory cap. SSE carries the raw result before
     *  the backend reduces it for persistence (ADR-0023), so a single MCP page
     *  scrape or base64 image can be megabytes, and 400 of those is hundreds of
     *  MB in a tab that mostly never opens the inspector. Cap per item and in
     *  aggregate, and only retain at all while the Developer Mode preference
     *  is on. */
    private static readonly MAX_CAPTURED_BYTES_PER_ITEM = 128 * 1024;
    private static readonly MAX_CAPTURED_BYTES_TOTAL = 8 * 1024 * 1024;
    private capturedBytes = 0;
    private readonly capturedSizes = new Map<string, number>();

    private static approxBytes(value: unknown): number {
        if (value === undefined || value === null) {
            return 0;
        }
        try {
            // 2 bytes per UTF-16 code unit is the right order of magnitude; this
            // is a budget, not an allocator.
            return JSON.stringify(value).length * 2;
        } catch {
            // Cyclic or non-serialisable: assume it is over budget rather than
            // retaining something we cannot measure.
            return DdrRoomStreamService.MAX_CAPTURED_BYTES_PER_ITEM + 1;
        }
    }

    private captureToolResult(toolUseId: string, captured: CapturedToolResult): void {
        if (!toolUseId) {
            return;
        }
        // The inspector is the only consumer, and it is Developer-Mode-only.
        // Retaining for everyone else buys nothing and costs the whole budget.
        // Gate on the persisted preference - the same signal canInspect()
        // reads - NOT SettingsService.developerModeRevealed, which is the
        // ephemeral five-clicks-on-Profile flag and false in normal use.
        if (!this.userPreferences.developerMode()) {
            return;
        }
        const size = DdrRoomStreamService.approxBytes(captured.result) + DdrRoomStreamService.approxBytes(captured.args);
        if (size > DdrRoomStreamService.MAX_CAPTURED_BYTES_PER_ITEM) {
            // Drop the payload but keep the envelope, so the chip still opens
            // and says why there is no body instead of looking broken.
            captured = { ...captured, result: { omitted: 'Result too large to retain for inspection' } };
        }
        this.releaseCaptured(toolUseId);
        const retained = DdrRoomStreamService.approxBytes(captured.result) + DdrRoomStreamService.approxBytes(captured.args);
        this.capturedBytes += retained;
        this.capturedSizes.set(toolUseId, retained);
        while (this.capturedBytes > DdrRoomStreamService.MAX_CAPTURED_BYTES_TOTAL && this.capturedResults.size > 0) {
            const oldest = this.capturedResults.keys().next();
            if (oldest.done || oldest.value === toolUseId) {
                break;
            }
            this.capturedResults.delete(oldest.value);
            this.releaseCaptured(oldest.value);
        }
        // Re-insert to move it to the end of the Map's insertion order, so
        // eviction is genuinely oldest-first.
        this.capturedResults.delete(toolUseId);
        this.capturedResults.set(toolUseId, captured);
        while (this.capturedResults.size > DdrRoomStreamService.MAX_CAPTURED_RESULTS) {
            const oldest = this.capturedResults.keys().next();
            if (oldest.done) {
                break;
            }
            this.capturedResults.delete(oldest.value);
            this.releaseCaptured(oldest.value);
        }
    }

    /** Drop one entry's contribution to the byte budget. Safe to call for an id
     *  that was never counted. */
    private releaseCaptured(toolUseId: string): void {
        const prior = this.capturedSizes.get(toolUseId);
        if (prior !== undefined) {
            this.capturedBytes -= prior;
            this.capturedSizes.delete(toolUseId);
            if (this.capturedBytes < 0) {
                this.capturedBytes = 0;
            }
        }
    }

    /** The captured args + raw result for a tool call, or null when nothing was
     *  captured (a turn from before this page load, or an evicted entry).
     *  Callers must treat null as "no detail available", never as an error. */
    capturedToolResult(toolUseId: string | undefined): CapturedToolResult | null {
        return (toolUseId && this.capturedResults.get(toolUseId)) || null;
    }

    constructor() {
        if (typeof document !== 'undefined') {
            const interactions = ['pointerdown', 'keydown', 'wheel', 'touchstart'];
            document.addEventListener('visibilitychange', this.flushPendingReadMarks);
            window.addEventListener('focus', this.flushPendingReadMarks);
            for (const type of interactions) {
                document.addEventListener(type, this.noteInteraction, { capture: true, passive: true });
            }
            inject(DestroyRef).onDestroy(() => {
                document.removeEventListener('visibilitychange', this.flushPendingReadMarks);
                window.removeEventListener('focus', this.flushPendingReadMarks);
                for (const type of interactions) {
                    document.removeEventListener(type, this.noteInteraction, { capture: true });
                }
            });
        }
        // Fetch personas only once the DDR release gate opens. At construction
        // auth is usually still loading (ddrEnabled=false) - fetching then
        // would 403 against the backend gate. The effect also re-fires the
        // fetch if the flag flips on mid-session.
        //
        // Keyed on the signed-in state as well: sign-out aborts the shared
        // user-events stream without completing it, so a subscription taken
        // in the previous session is dead, not closed. The next account to
        // sign in on the same tab must subscribe again or it never hears a
        // roster push (live 2026-09-22: three DDRs hired through Alice after
        // a sign-out, none on the sidebar until a reload).
        effect(() => {
            if (!this.signedIn()) {
                this.pendingReadMarks.clear();
                untracked(() => this.stopRosterSse());
                return;
            }
            if (this.auth.ddrEnabled()) {
                void this.refreshPersonas();
                this.startRosterSse();
                void this.seedActiveBuilds();
                // Unread badges must be right on first paint, not only after a
                // live SSE event arrives (audit-4 #52).
                void this.refreshUnread();
            }
        });
    }

    private readonly signedIn = computed(() => this.auth.state().status === 'authenticated');

    /** Live sidebar roster: a chat-driven hire/retire/adjust (Alice's manager
     *  tools) pushes `ddr_roster_changed` on the user-events pipe - refetch
     *  so the new DDR appears without a manual page refresh. */
    private rosterSse: Subscription | null = null;
    private startRosterSse(): void {
        if (this.rosterSse) {
            return;
        }
        this.rosterSse = this.threadEvents.connect().subscribe(evt => {
            if ((evt as { type?: string }).type === 'ddr_turn_frame') {
                this.onRemoteTurnFrame(evt as unknown as RemoteTurnFrameEvent);
                return;
            }
            const e = evt as {
                type?: string;
                action?: string;
                persona_id?: string;
                display_name?: string;
                avatar_url?: string | null;
                archetype?: string;
                role_title?: string;
                accent_color_hex?: string | null;
                room_id?: string;
                surface?: string;
                status?: string;
                build_id?: number;
            };
            // Global app-build in-flight signal (#482): the runner announces
            // 'building' when a build starts and its terminal status from the
            // outcome choke point. Drives the pulsing badge on the Apps rail
            // icon so a build stays visible after the modal is closed.
            if (e.type === 'app_build_state' && typeof e.build_id === 'number') {
                this._activeBuildIds.update(set => {
                    const next = new Set(set);
                    if (e.status === 'building') {
                        next.add(e.build_id!);
                    } else {
                        next.delete(e.build_id!);
                    }
                    return next;
                });
            }
            // A proactive/job message landed as a CHAT message in a room
            // (surface chat/both) - re-read the server's unread counts so that
            // room's sidebar pill badges without the user having the DM open.
            // Re-read rather than +1: a card that is still unread is EDITED in
            // place on the next run (one row, one unread), so a local bump
            // per event over-counts. Rail-only jobs don't badge: their room
            // chat is unchanged. If the room IS open, its panel reloads
            // history on this same event and marks the room read once the tab
            // is visible.
            if (e.type === 'proactive_activity' && e.room_id && (e.surface === 'chat' || e.surface === 'both')) {
                void this.refreshUnread();
            }
            // Alice's attention alert went up (DDR-211). The event carries no
            // state on purpose: the roster read is the one place the alert is
            // served from, so re-reading it keeps a live tab and a reloaded
            // one saying the same thing. The alert is raised by a tool-borrow
            // in some OTHER room, which is why nothing else here would notice.
            if (e.type === 'alice_attention') {
                void this.refreshPersonas();
            }
            if (e.type === 'ddr_roster_changed') {
                void this.refreshPersonas();
                if (e.action === 'hired' && typeof e.persona_id === 'string' && typeof e.display_name === 'string') {
                    this._newHire.set({
                        personaId: e.persona_id,
                        displayName: e.display_name,
                        avatarUrl: e.avatar_url ?? null,
                        archetype: e.archetype ?? undefined,
                        roleTitle: e.role_title ?? undefined,
                        accentColor: e.accent_color_hex ?? null,
                    });
                }
            }
        });
    }

    /** Sign-out: drop the dead subscription and the previous account's roster
     *  so the next sign-in subscribes afresh and never paints someone else's DDRs. */
    private stopRosterSse(): void {
        this.rosterSse?.unsubscribe();
        this.rosterSse = null;
        this.personaMap.set(new Map());
    }

    /** The latest chat-driven hire awaiting candidate confirmation. */
    private readonly _newHire = signal<{
        personaId: string;
        displayName: string;
        avatarUrl: string | null;
        archetype?: string;
        roleTitle?: string;
        accentColor?: string | null;
    } | null>(null);
    readonly newHire = this._newHire.asReadonly();

    /** Unread DDR-message count per room, seeded from the server watermark
     *  (GET /rooms/unread) and bumped live by SSE. Drives the numbered badge on
     *  the sidebar DDR pills.
     *
     *  Server-backed on purpose: unread has to survive a reload and an offline
     *  delivery. A DDR that messaged you while the tab was closed must still
     *  show up, and the count can't be derived from messages the client never
     *  fetched. */
    private readonly _unreadCounts = signal<ReadonlyMap<string, number>>(new Map());
    readonly unreadCounts = this._unreadCounts.asReadonly();

    /** Total across all rooms - one number for a global indicator. */
    readonly unreadTotal = computed(() => {
        let total = 0;
        for (const n of this._unreadCounts().values()) {
            total += n;
        }
        return total;
    });

    unreadCount(roomId: string): number {
        return this._unreadCounts().get(roomId) ?? 0;
    }

    /** Load the server's unread counts. Errors leave the current map alone: a
     *  failed fetch must not clear badges the user still needs to see. */
    async refreshUnread(): Promise<void> {
        try {
            const res = await this.ddr.getRoomUnread();
            this._unreadCounts.set(new Map(Object.entries(res?.unread ?? {})));
        } catch {
            // Keep whatever we had.
        }
    }

    /** Room id -> highest message id the user has actually had in view. While
     *  the panel is scrolled up, reloads must not advance the watermark past it,
     *  or a message the user never reached reads as seen on the next open. */
    private readonly readCeiling = new Map<string, number>();

    setReadCeiling(roomId: string, upToMessageId: number | null): void {
        if (upToMessageId == null) {
            this.readCeiling.delete(roomId);
        } else {
            this.readCeiling.set(roomId, upToMessageId);
        }
    }

    /** Read marks asked for while nobody was at the tab, by room: applied when
     *  the user is back. A tab that reloads on every job nudge must not mark
     *  messages read that nobody saw, whether it is hidden, unfocused on a
     *  second screen, or focused with the user away; each note then merges
     *  into the unread one instead of posting fresh. */
    private readonly pendingReadMarks = new Map<string, { upTo?: number; pinUnread: boolean }>();

    private lastInteractionAt = Date.now();

    private noteInteraction = (): void => {
        this.lastInteractionAt = Date.now();
        this.flushPendingReadMarks();
    };

    private userAway(): boolean {
        if (typeof document === 'undefined') {
            return false;
        }
        return document.visibilityState === 'hidden' || !document.hasFocus() || Date.now() - this.lastInteractionAt > PRESENCE_WINDOW_MS;
    }

    private flushPendingReadMarks = (): void => {
        if (this.userAway() || this.pendingReadMarks.size === 0) {
            return;
        }
        const pending = [...this.pendingReadMarks.entries()];
        this.pendingReadMarks.clear();
        for (const [roomId, mark] of pending) {
            this.markRoomRead(roomId, mark.upTo, { pinUnread: mark.pinUnread });
        }
    };

    /** Mark a room read up to `upToMessageId` (the newest message actually
     *  DISPLAYED). Clears locally then persists the watermark so the badge stays
     *  gone across reloads and other tabs.
     *
     *  The local clear is optimistic and deliberately reconciled afterwards: a
     *  live bump can land for a message NEWER than `upToMessageId` (SSE delivers
     *  it after the panel rendered, before this call), and zeroing the whole room
     *  would hide it even though the server still counts it unread. Re-reading
     *  the server counts after the write restores that residue instead of losing
     *  the notification until some later refresh. */
    markRoomRead(roomId: string, upToMessageId?: number, opts?: { pinUnread?: boolean }): void {
        if (this.userAway()) {
            const prior = this.pendingReadMarks.get(roomId);
            this.pendingReadMarks.set(roomId, {
                // No id means "everything shown", so it outranks any id.
                upTo:
                    !prior || prior.upTo == null || upToMessageId == null
                        ? prior
                            ? undefined
                            : upToMessageId
                        : Math.max(upToMessageId, prior.upTo),
                pinUnread: (prior?.pinUnread ?? false) || opts?.pinUnread === true,
            });
            return;
        }
        const ceiling = this.readCeiling.get(roomId);
        if (ceiling != null && (upToMessageId == null || upToMessageId > ceiling)) {
            upToMessageId = ceiling;
        }
        if (this._unreadCounts().has(roomId)) {
            this._unreadCounts.update(map => {
                const next = new Map(map);
                next.delete(roomId);
                return next;
            });
        }
        void this.ddr
            .markRoomRead(roomId, upToMessageId)
            .then(res => {
                // Pin the divider at the watermark this open REPLACED. Anything
                // the user had not seen sits above it; a later open (or a
                // reload) replaces nothing and the line is gone.
                const previous = res?.previous_last_read_message_id ?? 0;
                const hadUnread = typeof upToMessageId === 'number' ? upToMessageId > previous : true;
                if (!opts?.pinUnread) {
                    return this.refreshUnread();
                }
                this._unreadMarkers.update(map => {
                    const next = new Map(map);
                    if (hadUnread && previous > 0) {
                        next.set(roomId, previous);
                    } else {
                        next.delete(roomId);
                    }
                    return next;
                });
                return this.refreshUnread();
            })
            .catch(() => {
                // Non-destructive: a failed watermark write just means the badge
                // may come back on the next refresh, which is the safe direction.
            });
    }

    /** Room id -> the read watermark that the current open replaced. The panel
     *  draws its "New" divider above the first DDR-authored message with a
     *  higher id. Absent when the open found nothing unread. Set once per open
     *  and never moved by streaming, so the line stays where the user's reading
     *  actually stopped (John, 2026-09-15). */
    private readonly _unreadMarkers = signal<ReadonlyMap<string, number>>(new Map());
    readonly unreadMarkers = this._unreadMarkers.asReadonly();

    unreadMarker(roomId: string): number | null {
        return this._unreadMarkers().get(roomId) ?? null;
    }

    /** Build ids currently running anywhere for this user - SSE-fed
     *  (app_build_state), seeded once from GET /app_builds so a page reload
     *  mid-build still shows the badge. Terminal events delete; the outcome
     *  chat message + unread dot take over as the "done" signal. */
    private readonly _activeBuildIds = signal<ReadonlySet<number>>(new Set());
    readonly activeBuildCount = computed(() => this._activeBuildIds().size);
    private buildsSeeded = false;

    async seedActiveBuilds(): Promise<void> {
        if (this.buildsSeeded) {
            return;
        }
        try {
            const resp = (await this.ddr.listAppBuilds()) as { items?: { id: number; status?: string }[] } | null;
            const items = resp?.items ?? [];
            const running = new Set(items.filter(b => b.status === 'running' || b.status === 'queued').map(b => b.id));
            // Ids the server reports in a TERMINAL state. Seeding used to only
            // add, so a build whose finish event we missed (tab asleep, SSE
            // reconnect) stayed in the set forever and showed a badge for work
            // that had already completed. This fetch is the only chance to
            // reconcile, so it must remove as well as add (audit-4 N2).
            const finished = new Set(items.map(b => b.id).filter(id => !running.has(id)));
            this._activeBuildIds.update(set => {
                const next = new Set(set);
                for (const id of running) {
                    next.add(id);
                }
                for (const id of finished) {
                    next.delete(id);
                }
                // Ids the server didn't mention at all are left alone: a build
                // started after this fetch is legitimately unknown to it.
                return next;
            });
            // Only latch AFTER a successful read. Latching up front meant one
            // failed request disabled seeding for the rest of the session, and
            // SSE alone can't recover it: a build already running at page load
            // emits nothing until it terminates, so the badge just never showed.
            this.buildsSeeded = true;
        } catch {
            // Leave buildsSeeded false so the next caller retries.
        }
    }

    clearNewHire(): void {
        this._newHire.set(null);
    }

    private personaRefreshGeneration = 0;

    /** Refresh persona id→row map. Call after a new hire / retire. */
    async refreshPersonas(options: { fresh?: boolean } = {}): Promise<void> {
        if (!untracked(this.auth.ddrEnabled)) {
            return;
        }
        const generation = ++this.personaRefreshGeneration;
        try {
            const list = await this.ddr.listPersonas(options);
            if (generation === this.personaRefreshGeneration) {
                this.personaMap.set(new Map((list ?? []).map(p => [p.id, p])));
            }
        } catch {
            // Keep prior map on transient failure.
        }
        // Rides the roster refresh rather than living on its own schedule:
        // hiring or retiring is exactly when which Roles remain hireable
        // changes, and the catalog will eventually vary per user too.
        await this.refreshArchetypes();
    }

    private async refreshArchetypes(): Promise<void> {
        try {
            this.archetypeCatalog.set(await this.ddr.listArchetypes());
        } catch {
            // Keep the prior catalog on transient failure.
        }
    }

    personas(): Map<string, Persona> {
        return this.personaMap();
    }

    /** Apply a mutation response immediately, before the follow-up roster read.
     *  The read remains authoritative, but a transient refresh failure must not
     *  leave the successful edit invisible. */
    reconcilePersona(persona: Persona): void {
        this.personaMap.update(current => {
            const next = new Map(current);
            next.set(persona.id, persona);
            return next;
        });
    }

    /** Remove a retired persona immediately; refreshPersonas still follows to
     *  reconcile any server-side room membership changes. */
    removePersona(personaId: string): void {
        this.personaMap.update(current => {
            const next = new Map(current);
            next.delete(personaId);
            return next;
        });
    }

    /** The Roles this user can hire, in catalog order. */
    archetypes(): ArchetypeCatalogEntry[] {
        return this.archetypeCatalog();
    }

    /** Reactive read for a room's message list. */
    messages(roomId: string) {
        return computed(() => this.stateByRoom().get(roomId)?.messages ?? []);
    }

    sending(roomId: string) {
        return computed(() => this.stateByRoom().get(roomId)?.sending ?? false);
    }

    /** Skills that ran in the current or most recent turn of this room. */
    skillsUsed(roomId: string) {
        return computed(() => this.stateByRoom().get(roomId)?.skillsUsed ?? []);
    }

    error(roomId: string) {
        return computed(() => this.stateByRoom().get(roomId)?.error ?? null);
    }

    /** Text of a send the server refused with 409, for the panel to restore. */
    rejectedText(roomId: string) {
        return computed(() => this.stateByRoom().get(roomId)?.rejectedText ?? null);
    }

    clearRejectedText(roomId: string): void {
        this.update(roomId, s => (s.rejectedText === null ? s : { ...s, rejectedText: null }));
    }

    /** True while a scroll-up load is in flight - UI shows a small
     *  "loading older messages…" indicator above the chat list. */
    loadingMore(roomId: string) {
        return computed(() => this.stateByRoom().get(roomId)?.loadingMore ?? false);
    }

    /** Reactive read for the active canvas session in this room, or null. */
    canvasSession(roomId: string) {
        return computed(() => this.stateByRoom().get(roomId)?.canvasSession ?? null);
    }

    /** Imperatively clear the canvas session - called when the user submits
     *  or cancels the canvas so the UI closes immediately (optimistic). */
    clearCanvasSession(roomId: string): void {
        this.update(roomId, s => ({ ...s, canvasSession: null }));
    }

    /** Sync the canvas panel with the server's actual session (#192). The
     *  canvas_opened SSE event is the only other source and is never
     *  replayed, so without this a refresh drops a live canvas and an
     *  expired/agent-closed session lingers as an uneditable zombie.
     *  404 = no session → clear; transport errors leave state untouched. */
    async refreshCanvasSession(roomId: string): Promise<void> {
        try {
            const session = await this.ddr.getCanvasSession(roomId);
            const fields = mapCanvasFields(session.fields ?? []);
            this.update(roomId, s => ({ ...s, canvasSession: { title: session.title ?? '', fields, locked: !!session.locked } }));
        } catch (err) {
            if ((err as { status?: number })?.status === 404) {
                this.update(roomId, s => (s.canvasSession ? { ...s, canvasSession: null } : s));
            }
        }
    }

    /** Persist a canvas field edit to Valkey via HTTP so the agent's next
     *  get_canvas_state call sees the updated value. Called on input blur.
     *  Fire-and-forget - a 404 (session already closed) is silently ignored
     *  since the submit message carries the full field values anyway. */
    saveCanvasFieldToServer(roomId: string, fieldId: string, value: string | boolean): void {
        void this.ddr.patchCanvasField(roomId, fieldId, value).catch(() => {
            // Silently swallow 404 (canvas closed) and 409 (canvas locked).
            // The submit message is the authoritative source of truth.
        });
    }

    /** Merge user-edited field values into the canvas session so the
     *  submit message reflects what the user actually typed. Called by
     *  the canvas component on every field change. */
    updateCanvasField(roomId: string, fieldId: string, value: string | boolean): void {
        this.update(roomId, s => {
            if (!s.canvasSession) {
                return s;
            }
            return {
                ...s,
                canvasSession: {
                    ...s.canvasSession,
                    fields: s.canvasSession.fields.map(f => (f.id === fieldId ? { ...f, value } : f)),
                },
            };
        });
    }

    /** False once we've confirmed there are no more messages upstream -
     *  the chat panel uses this to hide the "load more" affordance and
     *  stop firing the scroll-up handler. */
    hasMore(roomId: string) {
        return computed(() => this.stateByRoom().get(roomId)?.hasMore ?? true);
    }

    /** Initial fetch - caller invokes once when a room opens. */
    async loadHistory(roomId: string, opts?: { preserveError?: boolean; pinUnread?: boolean }): Promise<void> {
        // Don't clobber an in-flight stream. The room-panel effect re-fires
        // loadHistory every time the user navigates back to a room; if a
        // turn is mid-stream the local state has temp negative-id slots +
        // partial content that aren't on the server yet, and replacing
        // `messages` here wipes them - the visible reply vanishes and the
        // user sees an empty room until the stream ends, which looks like
        // the chat is hung.
        //
        // Wrap signal reads in `untracked()` so the room-panel constructor
        // effect that called us doesn't end up depending on `stateByRoom`.
        const isSending = untracked(() => this.stateByRoom().get(roomId)?.sending ?? false);
        if (isSending) {
            return;
        }
        // Sync the canvas panel with the server's actual session state (#192):
        // restores a live canvas after a page refresh (canvas_opened SSE is
        // never replayed) and clears a zombie one whose backend session
        // expired or was closed elsewhere. Fire-and-forget alongside the
        // message fetch; failures leave current state untouched.
        void this.refreshCanvasSession(roomId);
        try {
            // First page is the server's max so the visible window has plenty
            // of real conversation even on heavy-proactivity rooms. Older turns
            // load on-demand via `loadOlder`. One-shot retry on transient
            // transport failures.
            type HistoryResp = { messages?: RoomMessage[]; items?: RoomMessage[] } | RoomMessage[] | null;
            let resp: HistoryResp = null;
            for (let attempt = 0; attempt < 2; attempt++) {
                try {
                    resp = (await this.ddr.getRoomMessages(roomId, { limit: HISTORY_PAGE_SIZE })) as HistoryResp;
                    break;
                } catch (err) {
                    if (attempt === 1) {
                        throw err;
                    }
                    await new Promise(r => setTimeout(r, 400));
                }
            }
            const list: RoomMessage[] = Array.isArray(resp) ? resp : (resp?.messages ?? resp?.items ?? []);
            // Backend returns newest-first (id DESC) - flip for chat (oldest top).
            // Also compute `args_summary` per tool_call from saved args JSON
            // so reloaded breadcrumbs match the live-stream rendering.
            const ordered = [...list].reverse().map(m => {
                if (!m.tool_calls?.length) {
                    return m;
                }
                return {
                    ...m,
                    tool_calls: m.tool_calls.map(tc => {
                        const raw = tc as typeof tc & { args?: Record<string, unknown> };
                        // Always re-compute client-side when raw args are
                        // available - the client knows the browser's tz
                        // and can localize timestamps in the summary.
                        if (!raw.args) {
                            return tc;
                        }
                        return { ...tc, args_summary: shortArgsSummary(raw.args), step: tc.step ?? stepCaption(tc.tool, raw.args) };
                    }),
                };
            });
            // Defense in depth: preserve LIVE (streaming=true) temp slots
            // - those are actively receiving deltas and the server hasn't
            // persisted them yet. Drop temps with streaming=false: those
            // are either completed (replaced by ddr_message_saved → real id)
            // or cancelled (backend's background insert just landed in the
            // server response we're about to apply). Preserving them
            // duplicates the row visually.
            this.update(roomId, s => {
                const tempTrail = s.messages.filter(m => m.id < 0 && m.streaming);
                return {
                    ...s,
                    messages: [...ordered, ...tempTrail],
                    // Preserve a terminal stream error (set by the 'error'
                    // event) across the post-stream history reload. An errored
                    // turn persists no server row, so clearing here would wipe
                    // the only signal the user has - leaving an empty chat with
                    // no reply and no error ("looks like nothing happened").
                    error: opts?.preserveError ? s.error : null,
                    // If the first page returned fewer rows than the server
                    // cap, there's nothing older to fetch.
                    hasMore: list.length >= HISTORY_PAGE_SIZE,
                    loadingMore: false,
                };
            });
            // The unread badge is consumed only once the history actually
            // DISPLAYED (audit): clearing before the fetch meant a failed
            // load (offline, 5xx) silently ate the only signal that a
            // proactive message existed. The watermark is the newest message we
            // just rendered, not "now", so anything that lands after this render
            // still counts as unread.
            const newestShown = ordered.reduce((max, m) => (m.id > max ? m.id : max), 0);
            // Only a room OPEN pins the "New" line. The reloads after a turn or
            // an archive would otherwise pin it above a reply the user just
            // watched stream in.
            this.markRoomRead(roomId, newestShown || undefined, { pinUnread: opts?.pinUnread === true });
        } catch (err) {
            this.update(roomId, s => ({
                ...s,
                error: err instanceof Error ? err.message : 'Failed to load messages',
            }));
        }
    }

    /** Scroll-up pagination - fetch the next-older page using the oldest
     *  persisted message id as a cursor. Prepends to `messages`, leaves
     *  any in-flight streaming slots untouched. No-op when already
     *  loading, when `hasMore` is false, or when no real (positive-id)
     *  messages are loaded yet to derive the cursor. */
    async loadOlder(roomId: string): Promise<void> {
        const snap = untracked(() => this.stateByRoom().get(roomId));
        if (!snap) {
            return;
        }
        if (snap.loadingMore || !snap.hasMore) {
            return;
        }
        // Find the oldest real (server-persisted) message id. Temp negative
        // ids belong to in-flight stream slots and aren't valid cursors.
        const oldest = snap.messages.find(m => m.id > 0);
        if (!oldest) {
            return;
        }
        this.update(roomId, s => ({ ...s, loadingMore: true, error: null }));
        try {
            const resp = (await this.ddr.getRoomMessages(roomId, {
                limit: HISTORY_PAGE_SIZE,
                before_id: oldest.id,
            })) as { messages?: RoomMessage[]; items?: RoomMessage[] } | RoomMessage[] | null;
            const list: RoomMessage[] = Array.isArray(resp) ? resp : (resp?.messages ?? resp?.items ?? []);
            const ordered = [...list].reverse().map(m => {
                if (!m.tool_calls?.length) {
                    return m;
                }
                return {
                    ...m,
                    tool_calls: m.tool_calls.map(tc => {
                        const raw = tc as typeof tc & { args?: Record<string, unknown> };
                        // Always re-compute client-side when raw args are
                        // available - the client knows the browser's tz
                        // and can localize timestamps in the summary.
                        if (!raw.args) {
                            return tc;
                        }
                        return { ...tc, args_summary: shortArgsSummary(raw.args), step: tc.step ?? stepCaption(tc.tool, raw.args) };
                    }),
                };
            });
            // Dedup just in case the server returns a row already present
            // locally (rare - concurrent edits - but cheap to guard).
            this.update(roomId, s => {
                const have = new Set(s.messages.map(m => m.id));
                const fresh = ordered.filter(m => !have.has(m.id));
                return {
                    ...s,
                    messages: [...fresh, ...s.messages],
                    loadingMore: false,
                    hasMore: list.length >= HISTORY_PAGE_SIZE,
                };
            });
        } catch (err) {
            this.update(roomId, s => ({
                ...s,
                loadingMore: false,
                error: err instanceof Error ? err.message : 'Failed to load older messages',
            }));
        }
    }

    /** Send + stream. Returns immediately; events update room state.
     *
     *  `opts.incognito` marks the optimistic rows with a local segment
     *  sentinel so a turn sent from a latched DM (ADR-0041) renders inside
     *  the grey box immediately — the post-turn history reload then replaces
     *  the sentinel with the server's real segment id. */
    async send(
        roomId: string,
        content: string,
        targetPersonaId: string | null,
        opts?: { incognito?: boolean; onAccepted?: () => void }
    ): Promise<boolean> {
        const trimmed = content.trim();
        if (!trimmed) {
            return true;
        }
        // `onAccepted` fires once, the moment the server has the message
        // (user_message_saved, or the 202 that queues it behind a live turn).
        // A caller whose next step only needs the message to exist, Handle
        // it recording its hand-off, must not wait for the whole turn.
        let acceptedNotified = false;
        const notifyAccepted = () => {
            if (!acceptedNotified) {
                acceptedNotified = true;
                opts?.onAccepted?.();
            }
        };
        const current = this.stateByRoom().get(roomId);
        if (current?.sending) {
            // Message queueing: a send while a turn is streaming is ACCEPTED
            // server-side (202) and drained into a follow-up turn on the
            // already-open stream. Render the message immediately with a
            // queued marker; the marker clears when queued_drain_start
            // arrives on the live stream (or on the post-turn history sync).
            const queuedSegment = opts?.incognito ? -1 : null;
            const queuedTempId = -Date.now();
            this.update(roomId, s => ({
                ...s,
                messages: [
                    ...s.messages,
                    {
                        id: queuedTempId,
                        sender_type: 'user',
                        content: trimmed,
                        created_at: new Date().toISOString(),
                        incognito_segment_id: queuedSegment,
                        queued: true,
                    },
                ],
            }));
            try {
                const ack = await this.ddr.streamRoomMessage(roomId, { content: trimmed, target_persona_id: targetPersonaId }, evt => {
                    // Race fallback: the running turn ended before the server
                    // saw this send, so it opened a fresh stream instead of
                    // answering 202. The live turn's events land here where no
                    // slot machinery exists - the post-EOF history reload
                    // below picks the persisted reply up. Acceptance still
                    // counts, or a hand-off on this path waits for the turn.
                    if (evt.type === 'user_message_saved') {
                        notifyAccepted();
                    }
                });
                if (ack && ack.queued) {
                    notifyAccepted();
                    if (ack.messageId) {
                        this.update(roomId, s => ({
                            ...s,
                            messages: s.messages.map(m => (m.id === queuedTempId ? { ...m, id: ack.messageId } : m)),
                        }));
                    }
                    return true;
                }
                // Not queued: the fallback stream above ran to completion.
                await this.loadHistory(roomId, { preserveError: true });
            } catch (err) {
                if (err instanceof StreamRejectedError && err.status === 402) {
                    // The gate runs BEFORE the server queues anything, so a second
                    // message can be refused while the first is still streaming.
                    // Without this the queued path reported the raw
                    // "stream request refused: 402" that the direct path no longer
                    // does, and the modal never opened.
                    this.applySpendRefusal(roomId, err, new Set([queuedTempId]), queuedTempId, trimmed);
                    return false;
                }
                this.update(roomId, s => ({
                    ...s,
                    messages: s.messages.filter(m => m.id !== queuedTempId),
                    error: err instanceof Error ? err.message : 'Failed to queue the message - try again.',
                }));
                return false;
            }
            return true;
        }
        const localSegment = opts?.incognito ? -1 : null;
        const userTempId = -Date.now();
        const ddrTempId = -Date.now() - 1;
        const ctrl = new AbortController();
        this.directShadows.delete(roomId);
        this.update(roomId, s => ({
            ...s,
            sending: true,
            error: null,
            skillsUsed: [],
            abort: ctrl,
            messages: [
                ...s.messages,
                {
                    id: userTempId,
                    sender_type: 'user',
                    content: trimmed,
                    created_at: new Date().toISOString(),
                    incognito_segment_id: localSegment,
                },
                {
                    id: ddrTempId,
                    sender_type: 'ddr',
                    content: '',
                    created_at: new Date().toISOString(),
                    streaming: true,
                    tool_calls: [],
                    incognito_segment_id: localSegment,
                },
            ],
        }));

        // Track the message slot currently receiving deltas. Starts as
        // the placeholder we just appended; the auto-chain handoff path
        // bumps this to a fresh slot when a second `routing_decision`
        // arrives mid-stream (PM → engineer baton pass).
        const activeRef: { id: number; routedPersonaId: string | null } = {
            id: ddrTempId,
            routedPersonaId: null,
        };
        let nextTempIdSeed = ddrTempId;
        const allTempIds = new Set<number>([ddrTempId]);
        let sawTerminal = false;
        let accepted = true;
        const sendStartedAt = Date.now() - 60_000;
        try {
            await this.ddr.streamRoomMessage(
                roomId,
                { content: trimmed, target_persona_id: targetPersonaId },
                evt => {
                    this.directFramesSeen.set(roomId, (this.directFramesSeen.get(roomId) ?? 0) + 1);
                    if (evt.type === 'user_message_saved') {
                        notifyAccepted();
                    }
                    if (evt.type === 'done' || evt.type === 'error') {
                        sawTerminal = true;
                    }
                    // Chained routing - second persona's turn starts. Spawn
                    // a new placeholder slot + flip the first to non-streaming.
                    if (evt.type === 'routing_decision' && (evt as { handoff_from?: string }).handoff_from) {
                        const newTempId = --nextTempIdSeed - 1;
                        allTempIds.add(newTempId);
                        const prevId = activeRef.id;
                        activeRef.id = newTempId;
                        activeRef.routedPersonaId = null;
                        this.update(roomId, s => ({
                            ...s,
                            messages: [
                                ...s.messages.map(m => (m.id === prevId ? { ...m, streaming: false } : m)),
                                {
                                    id: newTempId,
                                    sender_type: 'ddr',
                                    content: '',
                                    created_at: new Date().toISOString(),
                                    streaming: true,
                                    tool_calls: [],
                                    persona_id: (evt as { primary_persona_id?: string }).primary_persona_id ?? undefined,
                                    persona_display_name: (evt as { primary_display_name?: string }).primary_display_name ?? undefined,
                                },
                            ],
                        }));
                        return;
                    }
                    // Queue drain - the finished turn found queued messages and
                    // is starting ONE follow-up turn over the batch on this same
                    // stream. Finalize the previous slot, clear queued chips, and
                    // spawn a fresh streaming slot for the drain turn's reply.
                    if (evt.type === 'queued_drain_start') {
                        const newTempId = --nextTempIdSeed - 1;
                        allTempIds.add(newTempId);
                        const prevId = activeRef.id;
                        activeRef.id = newTempId;
                        activeRef.routedPersonaId = null;
                        this.update(roomId, s => ({
                            ...s,
                            skillsUsed: [],
                            messages: [
                                ...s.messages.map(m =>
                                    m.id === prevId ? { ...m, streaming: false } : m.queued ? { ...m, queued: false } : m
                                ),
                                {
                                    id: newTempId,
                                    sender_type: 'ddr',
                                    content: '',
                                    created_at: new Date().toISOString(),
                                    streaming: true,
                                    tool_calls: [],
                                },
                            ],
                        }));
                        return;
                    }
                    // Chained DDR has been kicked off - stamp a thinking hint
                    // on the active slot so the gap before the first model
                    // event is intelligible to the user.
                    if (evt.type === 'chain_thinking') {
                        const personaName = (evt as { persona_display_name?: string }).persona_display_name ?? 'the next DDR';
                        this.update(roomId, s => ({
                            ...s,
                            messages: s.messages.map(m =>
                                m.id === activeRef.id ? { ...m, thinkingHint: `${personaName} is working on this…` } : m
                            ),
                        }));
                        return;
                    }
                    this.handleEvent(roomId, evt, userTempId, activeRef.id, pid => (activeRef.routedPersonaId = pid));
                },
                ctrl.signal
            );
        } catch (err) {
            if (!ctrl.signal.aborted) {
                if (err instanceof StreamRejectedError) {
                    accepted = false;
                }
                this.reportSendFailure(roomId, err, allTempIds, userTempId, trimmed);
            }
        } finally {
            const wasAborted = ctrl.signal.aborted;
            this.update(roomId, s => ({
                ...s,
                sending: false,
                skillsUsed: [],
                abort: null,
                messages: s.messages.map(m =>
                    allTempIds.has(m.id)
                        ? {
                              ...m,
                              streaming: false,
                              persona_id: m.persona_id ?? activeRef.routedPersonaId ?? undefined,
                          }
                        : m
                ),
            }));
            // Post-stream history sync: picks up server-side system messages
            // (approval cards, handoff markers, etc) that arrived during the
            // turn. Runs ONCE here instead of on each per-agent `done` event,
            // so chained handoff slots aren't clobbered mid-stream.
            //
            // Special case for the cancel() safety-net abort: the stream went
            // silent after the Stop, so the server may be an older build that
            // persists the partial as a fire-and-forget task which may not
            // have hit the DB yet. Loading history immediately would clobber
            // the local partial with a server view that lacks it. Wait ~1.5s
            // so that insert has time to land. A Stop the server answered
            // (ddr_message_saved{cancelled:true} ... done) never arrives here
            // aborted; its partial was written before the stream moved on.
            if (wasAborted) {
                setTimeout(() => void this.loadHistory(roomId, { preserveError: true }), 1500);
            } else if (!sawTerminal) {
                // Stream ended without a terminal event: socket died (backend
                // kill/restart, proxy cut). Drop the half-rendered bubble and
                // enter the SAME recovery flow a refreshed page uses - it
                // detects a pending resume, shows the recovery bubble, and
                // re-attaches for live streaming when the turn is adopted.
                // recoverDroppedStream remains its internal fallback.
                this.update(roomId, s => ({
                    ...s,
                    messages: s.messages.filter(m => !allTempIds.has(m.id)),
                }));
                void this.loadHistory(roomId, { preserveError: true });
                void this.recoverIfTurnRunning(roomId);
            } else {
                void this.loadHistory(roomId, { preserveError: true });
            }
            // After this stream's own slots are settled, so a replayed successor
            // slot is never swept up with them.
            this.releaseDirectStream(roomId, Math.min(userTempId, ...allTempIds));
            // Spend changed this turn - let the budget banner re-check.
            this._turnCompleted.update(n => n + 1);
        }
        return accepted;
    }

    /** After a premature stream EOF, keep checking history for the reply the
     *  backend will persist when the (still-running) turn finishes. Stops as
     *  soon as a real DDR message from this turn window appears, or gives up
     *  after ~4 minutes with an honest error. */
    /** The persona most recently seen speaking in this room, for labels on a
     *  turn this tab did not start (a re-attach after a dropped stream or a
     *  deploy): turn_status does not name the persona and tool frames carry no
     *  name, so without this the bubble reads "DDR" until the first text frame. */
    private lastKnownPersonaName(roomId: string): string | undefined {
        const known = this.stateByRoom().get(roomId)?.messages ?? [];
        return [...known].reverse().find(m => m.sender_type === 'ddr' && !!m.persona_display_name)?.persona_display_name;
    }

    private async recoverDroppedStream(roomId: string, sinceMs: number): Promise<void> {
        const who = this.lastKnownPersonaName(roomId) ?? 'Your DDR';
        this.update(roomId, s => ({
            ...s,
            error: `Connection dropped. ${who} is still working, waiting for the reply...`,
        }));
        for (const delay of [2000, 6000, 15000, 30000, 60000, 120000]) {
            await new Promise(resolve => setTimeout(resolve, delay));
            const current = this.stateByRoom().get(roomId);
            if (current?.sending) {
                return; // user started a new turn; that stream owns the room now
            }
            await this.loadHistory(roomId, { preserveError: true });
            const st = this.stateByRoom().get(roomId);
            const landed = st?.messages.some(
                m =>
                    m.sender_type === 'ddr' && typeof m.id === 'number' && m.id > 0 && !!m.created_at && Date.parse(m.created_at) >= sinceMs
            );
            if (landed) {
                this.update(roomId, s => ({ ...s, error: null }));
                return;
            }
        }
        this.update(roomId, s => ({
            ...s,
            error: 'The connection dropped and no reply arrived yet. It may still show up, check back shortly or try again.',
        }));
    }

    /** During the restart->adoption gap: retry attach until the adopter
     *  registers the resumed turn (attach then streams live), or the turn
     *  completes out-of-band (status false; reply already in history).
     *  Returns true when the reply landed without a successful attach. */
    private async retryAttachDuringAdoption(
        roomId: string,
        ctrl: AbortController,
        onEvent: (evt: RoomStreamEvent) => void
    ): Promise<boolean> {
        for (let i = 0; i < 75; i++) {
            await new Promise(resolve => setTimeout(resolve, 4000));
            if (ctrl.signal.aborted) {
                return false;
            }
            try {
                await this.ddr.streamRoomAttach(roomId, onEvent, ctrl.signal);
                return false; // attached + streamed to completion
            } catch (attachErr) {
                const aMsg = attachErr instanceof Error ? attachErr.message : '';
                if (!aMsg.includes('404') && !aMsg.includes('409')) {
                    return false; // transport error - outer fallbacks own it
                }
            }
            let still = false;
            try {
                still = (await this.ddr.roomTurnStatus(roomId)).running;
            } catch {
                still = false;
            }
            if (!still) {
                return true; // reply landed (or degraded to a note)
            }
        }
        return false;
    }

    cancel(roomId: string): void {
        // Detached execution: the SERVER stops the current generation. It
        // persists the partial and announces it on this same stream as
        // ddr_message_saved{cancelled:true}, then, if the user queued messages
        // while the reply ran, follows with queued_drain_start and their reply
        // before the terminal done - all rendered by the stream's existing
        // handlers. So the subscription is deliberately NOT aborted here:
        // aborting would drop those frames and leave the queued messages
        // looking unanswered until a reload.
        // Safety net for a stream that goes silent after the Stop (a backend
        // predating this, a dead socket): if no frame lands within
        // CANCEL_SILENCE_ABORT_MS of the cancel request settling, abort the
        // read the old way so the panel does not sit on a spinner forever.
        const ctrl = this.stateByRoom().get(roomId)?.abort ?? null;
        const armSilenceAbort = () => {
            if (!ctrl || ctrl.signal.aborted || this.stateByRoom().get(roomId)?.abort !== ctrl) {
                return; // the stream already ended on its own
            }
            const seenAtCancel = this.directFramesSeen.get(roomId) ?? 0;
            setTimeout(() => {
                const stillOpen = this.stateByRoom().get(roomId)?.abort === ctrl;
                if (stillOpen && (this.directFramesSeen.get(roomId) ?? 0) === seenAtCancel) {
                    ctrl.abort();
                }
            }, CANCEL_SILENCE_ABORT_MS);
        };
        // best-effort POST: a failed cancel request still arms the safety net
        this.ddr.cancelRoomTurn(roomId).then(armSilenceAbort, armSilenceAbort);
    }

    /** After a refresh/tab-kill: re-attach to the room's LIVE turn. The
     *  server replays the buffered backlog then streams live, and we render
     *  it through the exact same event pipeline as a normal send - streaming
     *  bubble, tool chips, Stop button - indistinguishable from never having
     *  refreshed. No turn running (404) = silent no-op. */
    async recoverIfTurnRunning(roomId: string): Promise<void> {
        const existing = this.stateByRoom().get(roomId);
        if (existing?.sending) {
            return;
        }
        const ctrl = new AbortController();
        const ddrTempId = -Date.now();
        let nextTempIdSeed = ddrTempId;
        const allTempIds = new Set<number>([ddrTempId]);
        let sawTerminal = false;
        const sendStartedAt = Date.now() - 10 * 60_000;
        const activeRef = { id: ddrTempId, routedPersonaId: null as string | null };
        let attached = false;

        const markAttached = () => {
            if (attached) {
                return;
            }
            attached = true;
            this.update(roomId, s => ({
                ...s,
                sending: true,
                abort: ctrl,
                error: null,
                messages: [
                    ...s.messages,
                    {
                        id: ddrTempId,
                        sender_type: 'ddr',
                        content: '',
                        created_at: new Date().toISOString(),
                        streaming: true,
                        tool_calls: [],
                    },
                ],
            }));
        };

        try {
            await this.ddr.streamRoomAttach(
                roomId,
                evt => {
                    // Counted like direct-stream frames: Stop's silence fallback must
                    // not abort a reattached stream that is still delivering.
                    this.directFramesSeen.set(roomId, (this.directFramesSeen.get(roomId) ?? 0) + 1);
                    if (evt.type === 'user_message_saved') {
                        return; // already in loaded history; remapping would hit the wrong temp
                    }
                    markAttached();
                    if (evt.type === 'done' || evt.type === 'error') {
                        sawTerminal = true;
                    }
                    if (evt.type === 'routing_decision' && (evt as { handoff_from?: string }).handoff_from) {
                        const newTempId = --nextTempIdSeed - 1;
                        allTempIds.add(newTempId);
                        const prevId = activeRef.id;
                        activeRef.id = newTempId;
                        this.update(roomId, s => ({
                            ...s,
                            messages: [
                                ...s.messages.map(m => (m.id === prevId ? { ...m, streaming: false } : m)),
                                {
                                    id: newTempId,
                                    sender_type: 'ddr',
                                    content: '',
                                    created_at: new Date().toISOString(),
                                    streaming: true,
                                    tool_calls: [],
                                },
                            ],
                        }));
                        return;
                    }
                    this.handleEvent(roomId, evt, ddrTempId, activeRef.id, pid => (activeRef.routedPersonaId = pid));
                },
                ctrl.signal
            );
        } catch (err) {
            const msg = err instanceof Error ? err.message : '';
            if (msg.includes('404') || msg.includes('409')) {
                // The turn is unreachable from here, for either of two reasons:
                // 404, it is not in THIS replica's registry (another replica
                // runs it, or a checkpoint awaits adoption after a restart);
                // 409, this replica owns it but another subscriber holds the
                // stream. `roomTurnStatus` answers globally - it falls back to
                // the checkpoint row - so it can tell both apart from "no turn",
                // and its `owner` separates them again: 'remote' means another
                // replica is running it, so follow the ddr_turn_frame events on
                // the user pipe rather than attaching here.
                //
                // markAttached() is what renders `sending`, and `sending` is the
                // only thing gating the Stop button. Without it the user has a
                // running turn and no control (CIQ-1902). Do not replace the
                // retry below with recoverDroppedStream: that bails the moment
                // `sending` is true, so the two cannot be combined.
                let status: { running: boolean; owner?: 'local' | 'remote' | null };
                try {
                    status = await this.ddr.roomTurnStatus(roomId);
                } catch {
                    return;
                }
                if (!status.running) {
                    return; // genuinely nothing running - normal room open
                }
                if (status.owner === 'remote') {
                    this.armRemoteTurn(roomId);
                    return;
                }
                markAttached();
                this.update(roomId, s => ({
                    ...s,
                    messages: s.messages.map(m => (m.id === activeRef.id ? { ...m, thinkingHint: 'Working…' } : m)),
                }));
                const landed = await this.retryAttachDuringAdoption(roomId, ctrl, evt => {
                    this.directFramesSeen.set(roomId, (this.directFramesSeen.get(roomId) ?? 0) + 1);
                    if (evt.type === 'user_message_saved') {
                        return;
                    }
                    if (evt.type === 'done' || evt.type === 'error') {
                        sawTerminal = true;
                    }
                    this.handleEvent(roomId, evt, ddrTempId, activeRef.id, pid => (activeRef.routedPersonaId = pid));
                });
                if (landed) {
                    sawTerminal = true;
                }
                return;
            }
            // Transport error mid-stream: fall back to the dropped-stream poll
            // so the reply still lands. 404/409 no longer reach here - both are
            // handled above, where the turn is confirmed running and is either
            // followed on the user pipe or attached with Stop surfaced.
            if (!ctrl.signal.aborted && attached) {
                void this.recoverDroppedStream(roomId, sendStartedAt);
            }
        } finally {
            if (attached) {
                const wasAborted = ctrl.signal.aborted;
                this.update(roomId, s => ({
                    ...s,
                    sending: false,
                    skillsUsed: [],
                    abort: null,
                    messages: s.messages.map(m => (allTempIds.has(m.id) ? { ...m, streaming: false } : m)),
                }));
                if (wasAborted) {
                    setTimeout(() => void this.loadHistory(roomId, { preserveError: true }), 1500);
                } else if (!sawTerminal) {
                    void this.loadHistory(roomId, { preserveError: true });
                    void this.recoverDroppedStream(roomId, sendStartedAt);
                } else {
                    void this.loadHistory(roomId, { preserveError: true });
                }
                this.releaseDirectStream(roomId, Math.min(...allTempIds));
                this._turnCompleted.update(n => n + 1);
            }
        }
    }

    /** Live turn frames from the user-events pipe (backend `ddr/turn_events.py`).
     *  Renders a turn this tab is NOT streaming directly - sent from another
     *  device or tab, or running on another replica after a refresh - through
     *  the same `handleEvent` pipeline as a normal send. The direct stream stays
     *  authoritative: while `abort` is set this tab owns the turn and the mirror
     *  is ignored, or every delta would render twice; only a successor turn's
     *  frames are kept aside for replay (see DirectStreamShadow). */
    private onRemoteTurnFrame(e: RemoteTurnFrameEvent): void {
        const roomId = e.room_id;
        const frame = e.frame;
        if (typeof roomId !== 'string' || !frame || typeof frame.type !== 'string') {
            return;
        }
        if (this.retiredTurnIds.get(roomId)?.includes(e.turn_id)) {
            return; // publishers buffer independently; a retired turn's tail can arrive late
        }
        const state = untracked(() => this.stateByRoom().get(roomId));
        if (state?.abort) {
            this.shadowDirectStreamFrame(roomId, e);
            return;
        }
        let rt = this.remoteTurns.get(roomId);
        if (rt && rt.turnId !== null && rt.turnId !== e.turn_id) {
            if (!isNewerTurnStart(rt, e)) {
                return;
            }
            this.finishRemoteTurn(roomId, rt);
            rt = undefined;
        }
        if (!rt) {
            if (!REMOTE_TURN_START_TYPES.has(frame.type)) {
                return;
            }
            rt = this.beginRemoteTurn(roomId);
        }
        if (rt.turnId === null) {
            rt.turnId = e.turn_id;
            rt.turnGen = typeof e.turn_gen === 'number' ? e.turn_gen : null;
        }
        if (typeof e.seq === 'number') {
            if (e.seq <= rt.lastSeq) {
                return; // duplicate or reordered behind something already rendered
            }
            rt.lastSeq = e.seq;
        }
        this.applyRemoteFrame(roomId, rt, frame);
    }

    /** A mirror frame that arrived while this tab owns the direct stream. The
     *  first turn id seen is the direct turn's own (its frames are already on
     *  the direct stream, so nothing to keep). A DIFFERENT id whose start frame
     *  carries a generation newer than the direct turn's is a successor that
     *  began on another tab before this stream closed; its frames are buffered
     *  for `releaseDirectStream`. Frames without a generation, or not newer,
     *  are a lost-lease predecessor's tail and are dropped as before. */
    private shadowDirectStreamFrame(roomId: string, e: RemoteTurnFrameEvent): void {
        let shadow = this.directShadows.get(roomId);
        if (!shadow) {
            shadow = { ownTurnId: null, ownTurnGen: null, successorTurnId: null, successorGen: null, buffered: [] };
            this.directShadows.set(roomId, shadow);
        }
        const gen = typeof e.turn_gen === 'number' ? e.turn_gen : null;
        if (shadow.ownTurnId === null) {
            // Only a START frame identifies the direct turn. A lost-lease
            // predecessor's tail (a turn_ended, a late delta) can arrive first
            // and must not be adopted as "ours", or the tab's own turn would be
            // buffered as a successor and rendered a second time.
            if (!REMOTE_TURN_START_TYPES.has(e.frame.type)) {
                return;
            }
            shadow.ownTurnId = e.turn_id;
            shadow.ownTurnGen = gen;
            return;
        }
        if (e.turn_id === shadow.ownTurnId) {
            shadow.ownTurnGen ??= gen;
            return;
        }
        if (e.turn_id === shadow.successorTurnId) {
            if (shadow.buffered.length < SUCCESSOR_BUFFER_MAX) {
                shadow.buffered.push(e);
            }
            return;
        }
        if (!REMOTE_TURN_START_TYPES.has(e.frame.type) || gen === null) {
            return;
        }
        if (shadow.ownTurnGen !== null && gen <= shadow.ownTurnGen) {
            return;
        }
        if (shadow.successorGen !== null && gen <= shadow.successorGen) {
            return;
        }
        shadow.successorTurnId = e.turn_id;
        shadow.successorGen = gen;
        shadow.buffered = [e];
    }

    /** The direct stream has torn down (`abort` is cleared). Replay the
     *  successor frames kept while it was open into remote following, so a
     *  turn that began on another tab during the hand-over renders here.
     *  `lowestDirectTempId` is the smallest temp id that stream minted: the
     *  remote seed drops below it first, or a successor slot minted in the
     *  same millisecond would share an id with a just-finished direct slot
     *  and every delta would land in both. */
    private releaseDirectStream(roomId: string, lowestDirectTempId: number): void {
        const shadow = this.directShadows.get(roomId);
        this.directShadows.delete(roomId);
        if (!shadow || shadow.buffered.length === 0) {
            return;
        }
        this.remoteTempIdSeed = Math.min(this.remoteTempIdSeed, lowestDirectTempId);
        for (const e of shadow.buffered) {
            this.onRemoteTurnFrame(e);
        }
    }

    /** turn_status said a turn is running that this tab cannot attach to
     *  (another replica or another tab owns the stream). Show the working
     *  bubble now and accept whatever frames arrive next, whatever their type:
     *  a mid-turn joiner only sees the rest of the turn live, and the history
     *  reload on `turn_ended` fills in what it missed. */
    private armRemoteTurn(roomId: string): void {
        if (this.remoteTurns.has(roomId)) {
            return;
        }
        const rt = this.beginRemoteTurn(roomId);
        this.ensureRemoteSlot(roomId, rt);
    }

    /** Negative, strictly decreasing across every remote slot this service ever
     *  mints. A bare `-Date.now()` collided when two turns began in the same
     *  millisecond (a drain follow-up right behind its predecessor), and the
     *  successor's deltas landed in the predecessor's bubble. */
    private remoteTempIdSeed = -Date.now();

    private nextRemoteTempId(): number {
        this.remoteTempIdSeed = Math.min(this.remoteTempIdSeed, -Date.now()) - 1;
        return this.remoteTempIdSeed;
    }

    private beginRemoteTurn(roomId: string): RemoteTurn {
        const ddrTempId = this.nextRemoteTempId();
        const rt: RemoteTurn = {
            turnId: null,
            turnGen: null,
            lastSeq: 0,
            activeId: ddrTempId,
            allTempIds: new Set<number>([ddrTempId]),
            routedPersonaId: null,
            slotCreated: false,
            endPoll: null,
        };
        this.remoteTurns.set(roomId, rt);
        this.update(roomId, s => ({ ...s, sending: true, error: null, skillsUsed: [] }));
        // Safety net for a lost terminal (pipe reconnect mid-turn): ask the
        // server, and finish when it says the room is idle.
        rt.endPoll = setInterval(() => void this.pollRemoteTurnEnd(roomId, rt), REMOTE_TURN_END_POLL_MS);
        return rt;
    }

    private ensureRemoteSlot(roomId: string, rt: RemoteTurn): void {
        if (rt.slotCreated) {
            return;
        }
        rt.slotCreated = true;
        const personaName = this.lastKnownPersonaName(roomId);
        this.update(roomId, s => ({
            ...s,
            messages: [
                ...s.messages,
                {
                    id: rt.activeId,
                    sender_type: 'ddr',
                    content: '',
                    created_at: new Date().toISOString(),
                    streaming: true,
                    tool_calls: [],
                    persona_display_name: personaName,
                    thinkingHint: `${personaName ?? 'Your DDR'} is working on this…`,
                },
            ],
        }));
    }

    private newRemoteSlot(roomId: string, rt: RemoteTurn, extra: Partial<RoomMessage>, clearQueued: boolean): void {
        const newTempId = this.nextRemoteTempId();
        rt.allTempIds.add(newTempId);
        const prevId = rt.activeId;
        rt.activeId = newTempId;
        rt.routedPersonaId = null;
        rt.slotCreated = true;
        this.update(roomId, s => ({
            ...s,
            messages: [
                ...s.messages.map(m =>
                    m.id === prevId ? { ...m, streaming: false } : clearQueued && m.queued ? { ...m, queued: false } : m
                ),
                {
                    id: newTempId,
                    sender_type: 'ddr',
                    content: '',
                    created_at: new Date().toISOString(),
                    streaming: true,
                    tool_calls: [],
                    ...extra,
                },
            ],
        }));
    }

    private applyRemoteFrame(roomId: string, rt: RemoteTurn, frame: RoomStreamEvent): void {
        switch (frame.type) {
            case 'user_message_saved': {
                // The sender's device renders this from its own optimistic bubble;
                // here it is the first sight of the message. Once per id.
                const id = (frame as { message_id?: number }).message_id;
                const content = (frame as { content?: string }).content;
                if (typeof id !== 'number') {
                    return;
                }
                this.update(roomId, s =>
                    s.messages.some(m => m.id === id)
                        ? s
                        : {
                              ...s,
                              messages: [
                                  ...s.messages,
                                  { id, sender_type: 'user', content: content ?? '', created_at: new Date().toISOString() },
                              ],
                          }
                );
                return;
            }
            case 'turn_ended':
                this.finishRemoteTurn(roomId, rt);
                return;
            case 'done':
                // The producer emits exactly one `done`, then `turn_ended`, which
                // is what finalizes here.
                return;
            case 'routing_decision':
                this.ensureRemoteSlot(roomId, rt);
                if ((frame as { handoff_from?: string }).handoff_from) {
                    this.newRemoteSlot(
                        roomId,
                        rt,
                        {
                            persona_id: (frame as { primary_persona_id?: string }).primary_persona_id ?? undefined,
                            persona_display_name: (frame as { primary_display_name?: string }).primary_display_name ?? undefined,
                        },
                        false
                    );
                    return;
                }
                break;
            case 'queued_drain_start':
                this.update(roomId, s => ({ ...s, skillsUsed: [] }));
                this.newRemoteSlot(roomId, rt, {}, true);
                return;
            case 'chain_thinking': {
                this.ensureRemoteSlot(roomId, rt);
                const personaName = (frame as { persona_display_name?: string }).persona_display_name ?? 'the next DDR';
                this.update(roomId, s => ({
                    ...s,
                    messages: s.messages.map(m =>
                        m.id === rt.activeId ? { ...m, thinkingHint: `${personaName} is working on this…` } : m
                    ),
                }));
                return;
            }
            default:
                this.ensureRemoteSlot(roomId, rt);
        }
        this.handleEvent(roomId, frame, 0, rt.activeId, pid => (rt.routedPersonaId = pid), true);
    }

    private finishRemoteTurn(roomId: string, rt: RemoteTurn): void {
        if (this.remoteTurns.get(roomId) === rt) {
            this.remoteTurns.delete(roomId);
        }
        if (rt.turnId) {
            const retired = [...(this.retiredTurnIds.get(roomId) ?? []), rt.turnId].slice(-RETIRED_TURNS_KEPT);
            this.retiredTurnIds.set(roomId, retired);
        }
        if (rt.endPoll) {
            clearInterval(rt.endPoll);
            rt.endPoll = null;
        }
        // Every slot of this turn stops streaming, by temp id AND by the
        // streaming flag: `ddr_message_saved` re-keys the active slot to the
        // persisted row id before `turn_ended` arrives, so a temp-id match alone
        // left that bubble spinning until the history reload replaced it (and
        // for good when the reload failed).
        this.update(roomId, s => ({
            ...s,
            sending: false,
            skillsUsed: [],
            messages: s.messages.map(m =>
                rt.allTempIds.has(m.id) || m.streaming
                    ? { ...m, streaming: false, persona_id: m.persona_id ?? rt.routedPersonaId ?? undefined }
                    : m
            ),
        }));
        // Reconcile with the server: the persisted reply replaces the temp
        // slot, and anything a mid-turn joiner never saw streamed fills in.
        void this.loadHistory(roomId, { preserveError: true });
        this._turnCompleted.update(n => n + 1);
    }

    private async pollRemoteTurnEnd(roomId: string, rt: RemoteTurn): Promise<void> {
        let running = true;
        try {
            running = (await this.ddr.roomTurnStatus(roomId)).running;
        } catch {
            return;
        }
        if (!running && this.remoteTurns.get(roomId) === rt) {
            this.finishRemoteTurn(roomId, rt);
        }
    }

    private handleEvent(
        roomId: string,
        evt: RoomStreamEvent,
        userTempId: number,
        ddrTempId: number,
        capturePersona: (pid: string) => void,
        /** Another device's frames: only the sending tab opens the studio. */
        remote?: boolean
    ): void {
        // Thin dispatcher - each branch's logic lives in a focused helper so
        // this method stays under the cyclomatic-complexity gate.
        this.settleStudioOnTurnEnd(roomId, evt, !remote);
        switch (evt.type) {
            case 'user_message_saved':
                this.applyUserMessageSaved(roomId, evt, userTempId);
                break;
            case 'routing_decision':
                this.applyRoutingDecision(roomId, evt, ddrTempId, capturePersona);
                break;
            case 'text_start':
                this.applyTextStart(roomId, ddrTempId);
                break;
            case 'thinking_delta':
                this.applyThinkingDelta(roomId, ddrTempId);
                break;
            case 'text_delta':
                this.applyTextDelta(roomId, evt, ddrTempId);
                break;
            case 'grounding_correction':
                this.applyGroundingCorrection(roomId, evt, ddrTempId);
                break;
            case 'tool_call_start':
            case 'server_tool_call_start':
                this.applyToolCallStart(roomId, evt, ddrTempId, !remote);
                break;
            case 'tool_call':
                this.applyToolCall(roomId, evt, ddrTempId, !remote);
                break;
            case 'tool_result':
            case 'server_tool_result':
                this.applyToolResult(roomId, evt, ddrTempId, !remote);
                break;
            case 'ddr_message_saved':
                this.applyDdrMessageSaved(roomId, evt, ddrTempId);
                break;
            case 'spend_blocked':
                this.applySpendBlocked(roomId, evt);
                break;
            case 'error':
                this.applyStreamError(roomId, evt);
                break;
            case 'canvas_opened':
            case 'canvas_updated':
            case 'canvas_unlocked':
            case 'canvas_closed':
                this.handleCanvasEvent(roomId, evt);
                break;
            case 'skill_resolved':
                this.applySkillResolved(roomId, evt);
                break;
            // 'done' is a no-op here - history reloads once after the whole
            // stream completes (see the `finally` in `sendRoomMessage`), not
            // on each per-turn done event (which a handoff fires twice).
            // The draft-retraction nudges are matched by table inside the
            // helper, so four event names cost this switch nothing.
            default:
                this.applyDraftRetracted(roomId, evt, ddrTempId);
                break;
        }
    }

    /** A studio opened for a build that never started says so when the turn ends. */
    private settleStudioOnTurnEnd(roomId: string, evt: RoomStreamEvent, local: boolean): void {
        if (local && (evt.type === 'done' || evt.type === 'error')) {
            this.studioHandoff.turnEnded(roomId);
        }
    }

    /** Terminal upstream error (LLM down / timeout / 5xx). Clear the in-flight
     *  state and stop the streaming dots on the active slot NOW, off the event
     *  itself; a wedged or half-open stream would otherwise spin forever even
     *  though the error already arrived. */
    private applyStreamError(roomId: string, evt: RoomStreamEvent): void {
        const msg = (evt as { message?: string }).message ?? 'stream error';
        // A draft retracted mid-turn and then cut off by the error would stay
        // dimmed under a frozen "Checking those figures" line for the rest of
        // the session (review, PR #1567): the error is the terminal here, so
        // the hints go with the streaming state.
        this.update(roomId, s => ({
            ...s,
            error: msg,
            sending: false,
            skillsUsed: [],
            messages: s.messages.map(m =>
                m.streaming
                    ? ({
                          ...m,
                          streaming: false,
                          retractionHint: undefined,
                          thinkingHint: undefined,
                          _replaceOnNextText: false,
                      } as RoomMessage)
                    : m
            ),
        }));
    }

    /** A skill entered this turn: a `/use`, pinned or auto-loaded match
     *  resolved by the server, or one the DDR loaded itself via read_skill.
     *  Unmatched resolutions carry no name and are ignored. */
    private applySkillResolved(roomId: string, evt: RoomStreamEvent): void {
        const name = (evt as { skill_name?: string }).skill_name;
        if (typeof name !== 'string' || !name) {
            return;
        }
        this.update(roomId, s => ({
            ...s,
            skillsUsed: s.skillsUsed.includes(name) ? s.skillsUsed : [...s.skillsUsed, name],
        }));
    }

    private applyUserMessageSaved(roomId: string, evt: RoomStreamEvent, userTempId: number): void {
        const realId = (evt as { message_id?: number }).message_id;
        if (typeof realId === 'number') {
            this.update(roomId, s => ({
                ...s,
                messages: s.messages.map(m => (m.id === userTempId ? { ...m, id: realId } : m)),
            }));
        }
    }

    private applyRoutingDecision(roomId: string, evt: RoomStreamEvent, ddrTempId: number, capturePersona: (pid: string) => void): void {
        const pid = (evt as { primary_persona_id?: string }).primary_persona_id ?? null;
        const pname = (evt as { primary_display_name?: string }).primary_display_name ?? null;
        if (pid) {
            capturePersona(pid);
        }
        this.update(roomId, s => ({
            ...s,
            messages: s.messages.map(m =>
                m.id === ddrTempId
                    ? {
                          ...m,
                          persona_id: pid ?? m.persona_id,
                          persona_display_name: pname ?? m.persona_display_name,
                      }
                    : m
            ),
        }));
    }

    private applyTextStart(roomId: string, ddrTempId: number): void {
        // Mark the assistant message that a new text block is starting so the
        // next text_delta inserts a paragraph break before its first chunk.
        // Without this, sequential thinking-narration-toolcall-narration fuse.
        this.update(roomId, s => ({
            ...s,
            messages: s.messages.map(m => (m.id === ddrTempId ? ({ ...m, _newBlock: m.content.length > 0 } as RoomMessage) : m)),
        }));
    }

    private applyThinkingDelta(roomId: string, ddrTempId: number): void {
        // Model is producing thinking content (invisible to user by policy).
        // Stamp a hint on the slot so the user sees the persona is actively
        // working instead of just dots. Cleared on first text/tool delta.
        this.update(roomId, s => ({
            ...s,
            messages: s.messages.map(m => {
                if (m.id !== ddrTempId) {
                    return m;
                }
                if (m.content || (m.tool_calls?.length ?? 0) > 0) {
                    return m;
                }
                if (m.thinkingHint) {
                    return m;
                }
                const personaLabel = m.persona_display_name ?? 'DDR';
                return { ...m, thinkingHint: `${personaLabel} is thinking…` } as RoomMessage;
            }),
        }));
    }

    /** The backend's grounding backstop rewrote the reply after streaming it:
     *  the model asserted results it never fetched, the agent made it fetch, and
     *  `text` is the corrected final. The server persists ONLY the corrected
     *  text (ddr_router clears its text parts on this frame), so the live view
     *  must match: replace, do not append, or the user reads the fabricated
     *  answer above the real one until a reload (Evan, 2026-09-16). */
    private applyGroundingCorrection(roomId: string, evt: RoomStreamEvent, ddrTempId: number): void {
        const text = (evt as { text?: string }).text;
        if (typeof text !== 'string') {
            return;
        }
        this.update(roomId, s => ({
            ...s,
            messages: s.messages.map(m =>
                m.id === ddrTempId
                    ? ({ ...m, content: text, _newBlock: false, _replaceOnNextText: false, retractionHint: undefined } as RoomMessage)
                    : m
            ),
        }));
    }

    /** The agent retracted the text it just streamed and is re-feeding the
     *  model (a figure no tool produced, a claim no tool grounds, a promise in
     *  place of an action). The server persists only what the re-fed turn
     *  writes, so the live view drops the draft NOW and says what is happening,
     *  instead of showing the retracted answer for the whole re-run and
     *  swapping it at the end (feature1, 2026-09-30: 90 s of a first answer
     *  that then vanished). The next text_delta clears the hint. */
    private applyDraftRetracted(roomId: string, evt: RoomStreamEvent, ddrTempId: number): void {
        const hint = DRAFT_RETRACTION_HINTS[evt.type];
        if (!hint) {
            return;
        }
        // Dim, do not blank: the reader keeps the text they were reading and
        // sees why the tool chips appeared under it. `_replaceOnNextText` makes
        // the re-fed answer take the draft's place instead of appending.
        this.update(roomId, s => ({
            ...s,
            messages: s.messages.map(m =>
                m.id === ddrTempId ? ({ ...m, retractionHint: hint, _replaceOnNextText: true, _newBlock: false } as RoomMessage) : m
            ),
        }));
    }

    private applyTextDelta(roomId: string, evt: RoomStreamEvent, ddrTempId: number): void {
        const delta = (evt as { delta?: string }).delta ?? '';
        if (!delta) {
            return;
        }
        this.update(roomId, s => ({
            ...s,
            messages: s.messages.map(m => {
                if (m.id !== ddrTempId) {
                    return m;
                }
                // Insert a paragraph break when a fresh text block starts
                // mid-turn (after a tool call), so multiple thinking-then-
                // narration segments don't fuse into one wall of text.
                if ((m as { _replaceOnNextText?: boolean })._replaceOnNextText) {
                    // First text after a retraction: the re-fed answer takes the
                    // dimmed draft's place.
                    return {
                        ...m,
                        content: delta,
                        _replaceOnNextText: false,
                        _newBlock: false,
                        retractionHint: undefined,
                        thinkingHint: undefined,
                    } as RoomMessage;
                }
                const sep =
                    m.content && !m.content.endsWith('\n\n') && (m as { _newBlock?: boolean })._newBlock
                        ? m.content.endsWith('\n')
                            ? '\n'
                            : '\n\n'
                        : '';
                return {
                    ...m,
                    content: m.content + sep + delta,
                    _newBlock: false,
                    thinkingHint: undefined,
                } as RoomMessage;
            }),
        }));
    }

    private applyToolCallStart(roomId: string, evt: RoomStreamEvent, ddrTempId: number, local: boolean): void {
        // Append the running placeholder. `tool_call` (with args) arrives next
        // for the same call - ignored for the breadcrumb since this already
        // created the entry. Server-side tools (Anthropic-hosted code
        // execution) emit `server_tool_call_start` and render the same pill so
        // the user SEES code execution instead of trusting "I ran it".
        const tool = (evt as { tool?: string }).tool ?? '?';
        // tool_use_id is carried onto the chip so the Tool-call inspector can
        // match it against captured detail (ADR-0023). Stamped here, at entry
        // creation, rather than in applyToolCall - that handler returns early
        // for calls with no meaningful args summary, which would leave those
        // chips permanently unmatchable.
        const toolUseId = (evt as { tool_use_id?: string }).tool_use_id;
        // A build opens the studio the moment the DDR starts the call, not after
        // it finishes writing the request.
        if (local && tool === 'start_app_build') {
            const slot = this.stateByRoom()
                .get(roomId)
                ?.messages.find(m => m.id === ddrTempId);
            this.studioHandoff.buildStarting(
                roomId,
                toolUseId ?? null,
                slot?.persona_display_name ?? this.lastKnownPersonaName(roomId) ?? ''
            );
        }
        this.update(roomId, s => ({
            ...s,
            messages: s.messages.map(m =>
                m.id === ddrTempId
                    ? {
                          ...m,
                          tool_calls: [...(m.tool_calls ?? []), { tool, tool_use_id: toolUseId, running: true }],
                          thinkingHint: undefined,
                      }
                    : m
            ),
        }));
    }

    private applyToolCall(roomId: string, evt: RoomStreamEvent, ddrTempId: number, local: boolean): void {
        // tool_call_start already added the placeholder; this event carries
        // args. Stamp a one-line summary onto the matching running entry so
        // the breadcrumb shows WHAT was searched / sent, not just the name.
        const tool = (evt as { tool?: string }).tool ?? '?';
        const args = (evt as { args?: Record<string, unknown> }).args ?? {};

        // Capture BEFORE the summary early-return: a call whose args produce no
        // one-line summary is often exactly the one worth inspecting.
        const toolUseId = (evt as { tool_use_id?: string }).tool_use_id;
        if (local && tool === 'start_app_build') {
            this.studioHandoff.buildArgs(toolUseId ?? null, typeof args['app_id'] === 'string' && args['app_id'] ? args['app_id'] : null);
        }
        if (toolUseId) {
            this.captureToolResult(toolUseId, {
                tool,
                args,
                // Filled in by applyToolResult; `undefined` distinguishes
                // "still running" from a tool that genuinely returned null.
                result: undefined,
                isError: false,
            });
        }

        const summary = shortArgsSummary(args);
        const step = (evt as { step?: string }).step?.trim() || stepCaption(tool, args);
        if (!summary && !step) {
            return;
        }
        this.update(roomId, s => ({
            ...s,
            messages: s.messages.map(m => {
                if (m.id !== ddrTempId) {
                    return m;
                }
                const calls = [...(m.tool_calls ?? [])];
                const idx = findChipIndex(calls, toolUseId, c => c.tool === tool && !!c.running && !c.args_summary && !c.step);
                if (idx >= 0) {
                    calls[idx] = { ...calls[idx], ...(summary ? { args_summary: summary } : {}), ...(step ? { step } : {}) };
                }
                // The step is the bubble's status line until the next step or
                // the reply's first text replaces it. Without it a long run is
                // ten minutes of dots.
                return step ? { ...m, tool_calls: calls, thinkingHint: step } : { ...m, tool_calls: calls };
            }),
        }));
    }

    private applyToolResult(roomId: string, evt: RoomStreamEvent, ddrTempId: number, local: boolean): void {
        const tool = (evt as { tool?: string }).tool ?? '?';
        const isError = !!(evt as { is_error?: boolean }).is_error;
        const result = (evt as { result?: Record<string, unknown> }).result;
        const queued = !!(result && result['queued']);
        // The server attaches its own label to every tool_result frame. A frame
        // from the user pipe (another device or tab) carries NO raw result, so
        // that label is the only truthful source there: deriving from an absent
        // result rendered blocked, suppressed and queued actions as "ok".
        const serverLabel = (evt as { result_label?: string }).result_label;
        // Incognito-only source refusal (ADR-0039). Only the marker and the
        // source's plain name come off the payload — same two fields the
        // backend persists onto the saved tool_calls JSON, so the offer reads
        // identically live and after a reload. The server also attaches them
        // to the frame itself for viewers without the raw result.
        const requiresIncognito = !!(
            (result && result['requires_incognito']) ||
            (evt as { requires_incognito?: boolean }).requires_incognito
        );
        const sourceName = requiresIncognito ? String(result?.['source_name'] ?? (evt as { source_name?: string }).source_name ?? '') : '';

        // The only place the raw result is ever available - the backend reduces
        // it to a summary string before persisting (ADR-0023). Keep the args
        // captured by applyToolCall; server-side tools emit no `tool_call`
        // event, so args may legitimately be absent.
        const toolUseId = (evt as { tool_use_id?: string }).tool_use_id;
        if (toolUseId) {
            this.captureToolResult(toolUseId, {
                tool,
                args: this.capturedResults.get(toolUseId)?.args ?? null,
                result,
                isError,
            });
        }

        // A build the DDR starts opens in the app studio, where it streams,
        // previews and takes follow-up changes. The studio opened at the call's
        // start swaps to the app; without one, the result opens it.
        if (tool === 'start_app_build') {
            const appId = !isError && result && typeof result['app_id'] === 'string' ? String(result['app_id']) : null;
            const handled = local && this.studioHandoff.buildResult(toolUseId ?? null, appId);
            if (!handled && appId) {
                void this.router.navigate(['/chat/workspace/apps'], {
                    queryParams: { app: appId },
                    state: { studioReturnTo: this.router.url },
                });
            }
        }

        this.update(roomId, s => ({
            ...s,
            messages: s.messages.map(m => {
                if (m.id !== ddrTempId) {
                    return m;
                }
                const calls = [...(m.tool_calls ?? [])];
                const idx = findChipIndex(calls, toolUseId, c => c.tool === tool && !!c.running);
                if (idx >= 0) {
                    calls[idx] = {
                        ...calls[idx],
                        running: false,
                        // Derived from the real result object so the live chip
                        // matches what the backend will have persisted by the
                        // time this turn is reloaded.
                        result_label: serverLabel ?? (queued ? 'queued for approval' : labelToolResult(result, isError)),
                        is_error: isError,
                        ...(requiresIncognito ? { requires_incognito: true, source_name: sourceName } : {}),
                    };
                }
                return { ...m, tool_calls: calls };
            }),
        }));
    }

    private applyDdrMessageSaved(roomId: string, evt: RoomStreamEvent, ddrTempId: number): void {
        const realId = (evt as { message_id?: number }).message_id;
        const finalContent = (evt as { content?: string }).content;
        this.update(roomId, s => ({
            ...s,
            messages: s.messages.map(m =>
                m.id === ddrTempId
                    ? {
                          ...m,
                          id: typeof realId === 'number' ? realId : m.id,
                          content: finalContent ?? m.content,
                          retractionHint: undefined,
                      }
                    : m
            ),
        }));
    }

    private applySpendBlocked(roomId: string, evt: RoomStreamEvent): void {
        // The turn never ran - the user is over a spend limit. Stop the sending
        // state and raise the app-level block so the chat shows an intentional
        // modal (copy chosen by limit_kind), not a bubble.
        const e = evt as { limit_kind?: string; reason?: string; resets?: string; plan_block?: unknown };
        this._spendBlock.set({
            limitKind: e.limit_kind ?? null,
            reason: e.reason ?? '',
            resets: e.resets ?? '',
            planBlock: e.plan_block ?? null,
        });
        this.update(roomId, s => ({ ...s, sending: false, skillsUsed: [] }));
    }

    /** How a send that never opened a stream is reported to the user. Three
     *  outcomes, each with its own remedy; the send loop itself only decides
     *  whether the message was accepted. */
    private reportSendFailure(roomId: string, err: unknown, allTempIds: Set<number>, userTempId: number, text: string): void {
        const msg = err instanceof Error ? err.message : 'stream failed';
        if (err instanceof StreamRejectedError && err.status === 402) {
            // The router spend gate refused the turn before persisting
            // anything (solvency, an ended trial, a plan block). Raise the same
            // modal the in-loop gate's `spend_blocked` event raises and hand the
            // text back, instead of leaving the raw "stream request refused:
            // 402" in the room error line with the message gone (CIQ-2129).
            this.applySpendRefusal(roomId, err, allTempIds, userTempId, text);
            return;
        }
        if (msg.includes('409')) {
            // A turn is already running server-side. Drop the temps, TELL the
            // user (never silent), try to attach to the live turn, and refresh
            // history either way - if the turn finished in the race window the
            // reply is already there.
            this.update(roomId, s => ({
                ...s,
                messages: s.messages.filter(m => !allTempIds.has(m.id) && m.id !== userTempId),
                error: 'Your DDR is still finishing the previous reply - your message is back in the box, send it again in a moment.',
                rejectedText: text,
            }));
            void this.recoverIfTurnRunning(roomId).then(() => {
                void this.loadHistory(roomId, { preserveError: true });
            });
            return;
        }
        this.update(roomId, s => ({ ...s, error: msg }));
    }

    /** A 402 on the stream POST: the router spend gate refused the turn. Raise
     *  the block modal, drop the optimistic bubbles the send had added, and put
     *  the text back in the composer so it is not lost (the 409 path's
     *  `rejectedText` contract). No `error` is set — the modal IS the
     *  explanation, and an error line under it reads as a second failure.
     *
     *  A 402 whose body carried no parseable detail still blocks, on the
     *  server's status alone: the alternative is the silent disappearance this
     *  fixes. `limitKind` is then null and the modal shows its generic copy. */
    private applySpendRefusal(roomId: string, err: StreamRejectedError, allTempIds: Set<number>, userTempId: number, text: string): void {
        const d = err.detail;
        this._spendBlock.set({
            limitKind: typeof d?.limit_kind === 'string' ? d.limit_kind : null,
            reason: typeof d?.message === 'string' ? d.message : '',
            resets: typeof d?.resets === 'string' ? d.resets : '',
            planBlock: d?.plan_block ?? null,
        });
        this.update(roomId, s => ({
            ...s,
            messages: s.messages.filter(m => !allTempIds.has(m.id) && m.id !== userTempId),
            rejectedText: text,
        }));
    }

    private handleCanvasEvent(roomId: string, evt: RoomStreamEvent): void {
        if (evt.type === 'canvas_opened') {
            const title = (evt as { title?: string }).title ?? '';
            const fields = mapCanvasFields((evt as { fields?: unknown[] }).fields ?? []);
            this.update(roomId, s => ({ ...s, canvasSession: { title, fields, locked: false } }));
        } else if (evt.type === 'canvas_closed') {
            // Agent-side dismiss (close_canvas tool): the backend session is
            // already deleted; drop the panel immediately.
            this.update(roomId, s => ({ ...s, canvasSession: null }));
        } else if (evt.type === 'canvas_updated') {
            const fieldId = (evt as { field_id?: string }).field_id ?? '';
            const newValue = (evt as { new_value?: string | boolean }).new_value ?? '';
            const diff = (evt as { diff?: CanvasDiffChunk[] }).diff ?? [];
            this.update(roomId, s => {
                if (!s.canvasSession) {
                    return s;
                }
                return {
                    ...s,
                    canvasSession: {
                        ...s.canvasSession,
                        locked: true,
                        fields: s.canvasSession.fields.map(f => (f.id === fieldId ? { ...f, value: newValue, diff } : f)),
                    },
                };
            });
        } else if (evt.type === 'canvas_unlocked') {
            this.update(roomId, s => {
                if (!s.canvasSession) {
                    return s;
                }
                return {
                    ...s,
                    canvasSession: {
                        ...s.canvasSession,
                        locked: false,
                        fields: s.canvasSession.fields.map(f => ({ ...f, diff: undefined })),
                    },
                };
            });
        }
    }

    private update(roomId: string, mut: (state: RoomState) => RoomState): void {
        this.stateByRoom.update(map => {
            const next = new Map(map);
            const prev = next.get(roomId) ?? EMPTY_STATE;
            next.set(roomId, mut(prev));
            return next;
        });
    }
}

/** ISO-8601 UTC timestamp detection. Mirrors the backend
 *  `_ISO_UTC_RE` so the breadcrumb knows when to localize. */
const ISO_UTC_RE = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}(?:\.\d{1,7})?(?:Z|\+00:00|\+0000)?$/;

/** Normalize raw canvas field dicts (from the canvas_opened SSE event or the
 *  GET /canvas restore endpoint - same backend shape) into CanvasField. */
function mapCanvasFields(rawFields: unknown[]): CanvasField[] {
    return rawFields.map((f: unknown) => {
        const field = f as Record<string, unknown>;
        return {
            id: String(field['id'] ?? ''),
            label: String(field['label'] ?? field['id'] ?? ''),
            type: String(field['type'] ?? 'text_field') as CanvasField['type'],
            value: field['value'] !== undefined ? (field['value'] as string | boolean | null) : null,
            required: Boolean(field['required'] ?? false),
            readonly: Boolean(field['readonly'] ?? false),
        };
    });
}

function formatBreadcrumbTimestamp(value: string): string {
    if (!ISO_UTC_RE.test(value)) {
        return value;
    }
    const normalized = value.endsWith('Z') ? value.slice(0, -1) + '+00:00' : value;
    const dt = new Date(normalized);
    if (isNaN(dt.getTime())) {
        return value;
    }
    try {
        return new Intl.DateTimeFormat(undefined, {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            timeZoneName: 'short',
        }).format(dt);
    } catch {
        return value;
    }
}

/** Pull the most-relevant arg out for the breadcrumb. Mirrors the backend
 *  `_short_args_summary` so the saved tool_calls JSON renders the same
 *  shape on history reload as the live stream does. */
const STEP_MAX_CHARS = 160;

/** Mirror of the backend's step_caption (run_code_tools.py): the call's `step`
 *  argument, else run_code's first comment line, else nothing. Used for saved
 *  tool_calls, which carry args but predate the frame field. */
function stepCaption(tool: string, args: Record<string, unknown> | undefined): string | undefined {
    if (!args) {
        return undefined;
    }
    const clip = (t: string) => (t.length > STEP_MAX_CHARS ? `${t.slice(0, STEP_MAX_CHARS - 1)}…` : t);
    const step = args['step'];
    if (typeof step === 'string' && step.trim()) {
        return clip(step.trim().replace(/\s+/g, ' '));
    }
    const code = args['code'];
    if (tool === 'run_code' && typeof code === 'string') {
        const first = code.split('\n').find(line => line.trim());
        if (first && first.trim().startsWith('#')) {
            const text = first.trim().replace(/^#+\s*/, '');
            return text ? clip(text) : undefined;
        }
    }
    return undefined;
}

function shortArgsSummary(args: Record<string, unknown>): string {
    if (!args) {
        return '';
    }
    const preferred = ['query', 'q', 'jql', 'url', 'subject', 'title', 'topic'];
    for (const k of preferred) {
        const v = args[k];
        if (typeof v === 'string' && v.trim()) {
            const t = v.trim();
            return t.length > 80 ? `${t.slice(0, 79)}…` : t;
        }
    }
    const since = args['since'];
    const until = args['until'];
    if (typeof since === 'string' || typeof until === 'string') {
        const bits: string[] = [];
        if (typeof since === 'string') {
            bits.push(`since ${formatBreadcrumbTimestamp(since)}`);
        }
        if (typeof until === 'string') {
            bits.push(`until ${formatBreadcrumbTimestamp(until)}`);
        }
        return bits.join(' ');
    }
    return '';
}

/** Mirrors the backend `_label_tool_result` so a live chip reads exactly the
 *  same as it will after a history reload. Without this a call says "ok"
 *  while streaming and "4 items" after refresh, which looks like a bug.
 *  User-facing: never surfaces result field names or raw error text (ADR 0023). */
/** Locate the chip a live tool_call/tool_result event belongs to.
 *
 *  `tool_use_id` is the authoritative key and is present on every backend
 *  event; the name-based scan is the pre-tool_use_id fallback. Matching on the
 *  NAME first is deterministically wrong for two calls to the same tool in one
 *  model response. */
export function findChipIndex<T extends { tool_use_id?: string }>(
    calls: T[],
    toolUseId: string | undefined,
    fallbackMatches: (call: T) => boolean
): number {
    if (toolUseId) {
        const exact = calls.findIndex(c => c.tool_use_id === toolUseId);
        if (exact >= 0) {
            return exact;
        }
    }
    for (let i = calls.length - 1; i >= 0; i--) {
        if (fallbackMatches(calls[i])) {
            return i;
        }
    }
    return -1;
}

export function labelToolResult(result: unknown, isError: boolean): string {
    if (isError) {
        return 'failed';
    }
    if (!result || typeof result !== 'object' || Array.isArray(result)) {
        return 'ok';
    }
    const obj = result as Record<string, unknown>;
    if (obj['queued']) {
        return 'queued for approval';
    }
    if (obj['error']) {
        return 'failed';
    }
    // Gate outcomes, BEFORE the success shapes. Without these a refused send
    // (trust gate) or a suppressed one (muted contact) falls through to 'ok'.
    // `suppressed` results carry a `recipients` list, which would otherwise be
    // counted by the list-key loop below. `refused` also covers an
    // incognito-only source (ADR-0039).
    if (obj['refused']) {
        return 'blocked';
    }
    if (obj['suppressed']) {
        return 'not sent';
    }
    for (const key of ['messages', 'events', 'items', 'results']) {
        const value = obj[key];
        if (Array.isArray(value)) {
            return `${value.length} ${key}`;
        }
    }
    if (obj['sent']) {
        return 'sent';
    }
    if (obj['written']) {
        return 'written';
    }
    if (obj['created']) {
        return 'created';
    }
    return 'ok';
}

/** Text for the chip's outcome. Prefers the backend's user-facing
 *  `result_label`; turns predating that field only carry the agent-facing
 *  `result_preview`, which leaks two things a non-technical user should never
 *  see: result field names ("ok (loaded, rejected, hint)") and raw error text
 *  ("error: HTTP 429 rate_limit_exceeded"). Reduce both. Informative previews
 *  ("4 items", "sent", "queued for approval") pass through untouched. */
export function chipResultText(tc: { result_label?: string; result_preview?: string }): string {
    if (tc.result_label) {
        return tc.result_label;
    }
    const preview = (tc.result_preview ?? '').trim();
    if (/^error\b/i.test(preview)) {
        return 'failed';
    }
    if (/^ok\s*\(.*\)$/.test(preview)) {
        return 'ok';
    }
    return preview;
}
